import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
  Divider,
  InputLabel,
  CircularProgress,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Link,
  InputAdornment,
  Switch,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { GitHub as GitHubIcon, Close as CloseIcon } from '@mui/icons-material';
import { useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import type { Tenant } from '../../App';
import { Check as CheckIcon } from '@mui/icons-material';


export enum GQLProviderType {
  GCP = "GCP",
  LANGFUSE = "LANGFUSE",
  AWS = "AWS",
  AZURE = "Azure",
}

// --- NEW GQL QUERY ---
// Added to check if self-hosted credentials are already set for a tenant.
const GET_LLM_OBSERVABILITY_CONFIG = gql`
  query GetLlmObservabilityConfig($tenantId: ID!) {
    getLlmObservabilityConfig(tenantId: $tenantId) {
      tenantId
      clickhouseUrl
    }
  }
`;

const CustomErrorIcon = () => (
    <Box
      component="span"
      sx={{
        width: 24,
        height: 24,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#D92D20',
      }}
    >
      <CloseIcon sx={{ color: 'white', fontSize: '16px' }} />
    </Box>
);

const GoBackDialog = ({ open, onClose, onConfirm }: { open: boolean, onClose: () => void, onConfirm: () => void }) => {
  if (!open) return null;

  const backgroundTextureUrl = '/textures/dialog-background-texture.svg';
  const goBackIconUrl = '/icons/go-back-dialog-icon.svg';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: '24px',
          width: '100%',
          maxWidth: '520px',
          p: 0.5,
          position: 'relative',
          overflow: 'hidden',
          backgroundImage: `url(${backgroundTextureUrl})`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'top right',
          backgroundColor: 'white',
        },
      }}
    >
      <DialogContent sx={{ p: 3, pt: 4, zIndex: 1 }}>
        <Box
          sx={{
            mb: 3,
            width: 50,
            height: 50,
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor:' #E5EFFF'
          }}
        >
          <img src={goBackIconUrl} alt="Go back icon" style={{ width: 19, height: 20 }} />
        </Box>
        <Typography sx={{ fontWeight: 600, mb: 1, fontSize: '16px', color: '#333333' }}>
          Go Back
        </Typography>
        <Typography variant="body1" sx={{ color: ' #333333', fontSize: '14px', lineHeight: 1.6, fontWeight:400 }}>
          Are you sure you want to leave this page? Leaving this page
          will discard all the progress made in the below form.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 3, justifyContent: 'flex-end', gap: 1.5, zIndex: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none',
            borderRadius: '8px',
            backgroundColor: '#EFF6FF',
            color: '#0064FF',
            fontWeight: 500,
            fontSize: '14px',
            padding: '10px 20px',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          size="large"
          sx={{
            textTransform: 'none',
            borderRadius: '8px',
            backgroundColor: '#0064FF',
            fontWeight: 500,
            fontSize: '14px',
            padding: '10px 20px',
            boxShadow: 'none',
            color:'white'
          }}
        >
          Yes, Go Back
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const LlmSetupGuide = () => {
    const [activeManager, setActiveManager] = useState('npm');

    const commands = {
        npm: 'npm install langfuse\n\n# Node.js < 18\nnpm install langfuse-node',
        pnpm: 'pnpm add langfuse\n\n# Node.js < 18\npnpm add langfuse-node',
        yarn: 'yarn add langfuse\n\n# Node.js < 18\nyarn add langfuse-node',
        bun: 'bun add langfuse\n\n# Node.js < 18\nbun add langfuse-node',
    };

    const Badge = ({ label, value, color, href }: { label: string, value: string, color: string, href?: string }) => (
        <Button
            component={href ? 'a' : 'button'}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
                p: 0,
                m: 0,
                height: '20px',
                textTransform: 'none',
                borderRadius: '4px',
                overflow: 'hidden',
                '&:hover': {
                    ...(href && {
                        opacity: 0.8
                    })
                }
            }}>
            <Box sx={{ bgcolor: '#555', color: 'white', px: 1, height: '100%', display: 'flex', alignItems: 'center' }}>
                {label === 'repo' && <GitHubIcon sx={{ fontSize: '14px', mr: 0.5 }} />}
                <Typography sx={{ fontSize: '10px', fontWeight: 500 }}>{label}</Typography>
            </Box>
            <Box sx={{ bgcolor: color, color: 'white', px: 1, height: '100%', display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ fontSize: '10px', fontWeight: 500 }}>{value}</Typography>
            </Box>
        </Button>
    );

    const TabButton = ({ manager }: { manager: 'npm' | 'pnpm' | 'yarn' | 'bun' }) => (
        <Button
            onClick={() => setActiveManager(manager)}
            sx={{
                p: '2px 8px',
                minWidth: 'auto',
                color: activeManager === manager ? 'primary.main' : 'text.secondary',
                fontWeight: 500,
                fontSize: '13px',
                borderBottom: 2,
                borderColor: activeManager === manager ? 'primary.main' : 'transparent',
                borderRadius: 0,
                textTransform: 'none'
            }}
        >
            {manager}
        </Button>
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>Langfuse SDK Setup</Typography>
          <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
              The Langfuse SDKs are the recommended way to integrate with Langfuse.{' '}
              <Link
                href="https://langfuse.com/integrations"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontWeight: 500 }}
              >
                [See a full list of Integration]
              </Link>
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Typography sx={{ fontSize: '12px', color: '#333333', fontWeight:600 }}>
              Install the Toolkit
          </Typography>
          <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
              Pick your language and run one of these commands in your project's terminal.
          </Typography>

          <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>JS/TS</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Badge label="repo" value="langfuse-js" color="#007ec6" href="https://github.com/langfuse/langfuse-js" />
              <Badge label="npm langfuse" value="v3.38.4" color="#007ec6" />
              <Badge label="npm langfuse-node" value="v3.38.4" color="#007ec6" />
          </Box>
          <Box sx={{ display: 'flex', borderBottom: 1, borderColor: 'divider' }}>
              <TabButton manager="npm" />
              <TabButton manager="pnpm" />
              <TabButton manager="yarn" />
              <TabButton manager="bun" />
          </Box>
          <Box sx={{ bgcolor: '#f7f7f7', p: 1.5, borderRadius: '4px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', fontSize: '12px', border: '1px solid #e0e0e0' }}>
              {commands[activeManager as keyof typeof commands]}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>Python</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Badge label="repo" value="langfuse-python" color="#007ec6" href="https://github.com/langfuse/langfuse-python" />
              <Badge label="pypi langfuse" value="v3.2.3" color="#007ec6" />
          </Box>
          <Box sx={{ bgcolor: '#f7f7f7', p: 1.5, borderRadius: '4px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', fontSize: '12px', border: '1px solid #e0e0e0', mt: 1 }}>
              pip install langfuse
          </Box>
        </Box>
    );
};

const GET_TENANT_PROVIDER_ACCESS = gql`
  query GetTenantProviderAccess($tenantId: ID!, $providerType: ProviderType!) {
    getTenantProviderAccess(tenantId: $tenantId, providerType: $providerType) {
      serviceAccountEmail
    }
  }
`;

const CREATE_DATA_SOURCE_MUTATION = gql`
  mutation CreateDataSource($input: CreateDataSourceInput!) {
    createDataSource(input: $input) {
      __typename
      ... on DataSource {
        id
        name
      }
      ... on LlmDataSource {
        id
        name
      }
    }
  }
`;

const VALIDATE_NEW_DATA_SOURCE_CREDENTIALS_MUTATION = gql`
  mutation ValidateNewDataSourceCredentials($input: ValidateNewDataSourceCredentialsInput!) {
    validateNewDataSourceCredentials(input: $input) {
      isValid
      validationErrors
    }
  }
`;

const mapProviderToGqlType = (frontendProviderValue: string): GQLProviderType => {
  switch (frontendProviderValue) {
    case 'google-cloud-platform': return GQLProviderType.GCP;
    case 'llm': return GQLProviderType.LANGFUSE;
    case 'aws': return GQLProviderType.AWS;
    case 'azure': return GQLProviderType.AZURE;
    default: throw new Error(`Unsupported provider value: ${frontendProviderValue}`);
  }
};

interface AddDataSourceOutletContext {
  currentTenant: Tenant | null;
}

const AddDataSourcesPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentTenant } = useOutletContext<AddDataSourceOutletContext>();
  const tenantId = currentTenant?.id;

  // Common State
  const [name, setName] = useState('');
  const [selectedProvider, setSelectedProvider] = useState(location.state?.defaultProvider || 'google-cloud-platform');

  // GCP State
  const [billingAccountId, setBillingAccountId] = useState('');
  const [bigQueryProjectId, setBigQueryProjectId] = useState('');
  const [bigQueryDatasetId, setBigQueryDatasetId] = useState('');
  const [bigQueryDataTableName, setBigQueryDataTableName] = useState('');
  const [permissionsGranted, setPermissionsGranted] = useState(false);
  const [serviceAccountEmail, setServiceAccountEmail] = useState<string | null>(null);

  // LLM State
  const [useOwnLangfuse, setUseOwnLangfuse] = useState(false);
  const [projectId, setProjectId] = useState('');
  const [clickhouseUrl, setClickhouseUrl] = useState('');
  // MODIFIED: Renamed state to align with new API
  const [clickhouseUsername, setClickhouseUsername] = useState('');
  const [clickhousePassword, setClickhousePassword] = useState('');

  // Error & UI State
  const [fieldErrors, setFieldErrors] = useState({ name: '', billingAccountId: '', bigQueryProjectId: '', bigQueryDatasetId: '', bigQueryDataTableName: '', projectId: '' });
  const [isGoBackDialogOpen, setIsGoBackDialogOpen] = useState(false);
  const [isToastOpen, setIsToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // GQL Hooks
  const [getTenantAccess, { loading: setupLoading, error: setupError }] = useLazyQuery(GET_TENANT_PROVIDER_ACCESS);
  const [createDataSource, { loading: createLoading }] = useMutation(CREATE_DATA_SOURCE_MUTATION);
  const [validateCredentials, { loading: validateLoading }] = useMutation(VALIDATE_NEW_DATA_SOURCE_CREDENTIALS_MUTATION);
  const [getLlmConfig, { data: llmConfigData, loading: llmConfigLoading }] = useLazyQuery(GET_LLM_OBSERVABILITY_CONFIG);

  const selfHostedConfigExists = !!llmConfigData?.getLlmObservabilityConfig?.clickhouseUrl;
  console.log("configtype--->", selfHostedConfigExists);
  // Effect to reset form on provider change
  useEffect(() => {
    // Reset all fields
    setName('');
    setBillingAccountId('');
    setBigQueryProjectId('');
    setBigQueryDatasetId('');
    setBigQueryDataTableName('');
    setPermissionsGranted(false);
    setUseOwnLangfuse(false);
    setProjectId('');
    setClickhouseUrl('');
    setClickhouseUsername('');
    setClickhousePassword('');
    setFieldErrors({ name: '', billingAccountId: '', bigQueryProjectId: '', bigQueryDatasetId: '', bigQueryDataTableName: '', projectId: '' });

    // Fetch GCP service account if needed
    if (tenantId && selectedProvider === 'google-cloud-platform') {
        getTenantAccess({
            variables: { tenantId, providerType: GQLProviderType.GCP },
            onCompleted: (data) => setServiceAccountEmail(data.getTenantProviderAccess?.serviceAccountEmail || null),
            onError: (error) => console.error("Failed to fetch service account email:", error),
        });
    } else {
        setServiceAccountEmail(null);
    }
  }, [selectedProvider, tenantId, getTenantAccess]);

  // MODIFIED: Effect to check for existing self-hosted config
  useEffect(() => {
    if (selectedProvider === 'llm' && useOwnLangfuse && tenantId) {
        getLlmConfig({ variables: { tenantId } });
    }
  }, [selectedProvider, useOwnLangfuse, tenantId, getLlmConfig]);

  const gcpStepsData = useMemo(() => {
    return [
        { step: 'STEP 1', title: 'Enable billing export to BigQuery', description: 'Go to Billing > Billing Export > Export to BigQuery > Choose Dataset' },
        {
          step: 'STEP 2',
          title: 'Assign Roles to Service Account',
          description: serviceAccountEmail
            ? `Give the following roles:\n * BigQuery Data Viewer\n * BigQuery Job User\nTo: The service account (${serviceAccountEmail})`
            : (setupLoading ? 'Fetching service account details...' : 'Could not retrieve service account.')
        },
        { step: 'STEP 3', title: 'Provide the following information below', description: 'Enter your GCP credentials and dataset details' },
    ];
  }, [serviceAccountEmail, setupLoading]);

  const externalLabelStyle = { fontFamily: 'Inter, sans-serif', fontSize: '12px', fontWeight: 400, color: theme.palette.text.primary, marginBottom: theme.spacing(0.75), display: 'block' };
  const sxTextField = {
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderRadius: '8px' },
      '&.Mui-focused fieldset': { borderColor: 'rgba(0, 0, 0, 0.23)' },
    },
    '& .MuiInputBase-input': {
      fontFamily: 'Inter, sans-serif', color: theme.palette.text.primary, fontSize: '12px',
      '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
        WebkitBoxShadow: '0 0 0 30px white inset !important', WebkitTextFillColor: `${theme.palette.text.primary} !important`,
        transition: 'background-color 5000s ease-in-out 0s',
      },
    },
    '& .MuiInputBase-input::placeholder': {
      fontFamily: 'Inter, sans-serif', color: 'rgba(185, 185, 185, 1)', fontSize: '12px', opacity: 1,
    },
  };

  const validateField = (name: string, value: string): string => {
    if (selectedProvider === 'llm') {
      if (name === 'name' && !/^[a-z0-9\s]{2,50}$/.test(value)) return 'Must be 2-50 chars, lowercase letters, numbers, spaces.';
      if (name === 'projectId' && value.trim() === '') return 'Project ID is required.';
      return '';
    }

    switch (name) {
      case 'name':
        if (!/^[a-z0-9\s]{2,50}$/.test(value)) return 'Must be 2-50 chars, lowercase letters, numbers, spaces.';
        break;
      case 'billingAccountId':
        if (!/^[0-9A-F]{6}-[0-9A-F]{6}-[0-9A-F]{6}$/i.test(value)) return 'Format: XXXXXX-XXXXXX-XXXXXX (hex digits).';
        break;
      case 'bigQueryProjectId':
        if (!/^[a-z][a-z0-9-]{4,28}[a-z0-9]$/.test(value)) return '6-30 chars, starts with letter, ends with letter/num, only lowercase, nums, hyphens.';
        break;
      case 'bigQueryDatasetId':
        if (!/^[a-zA-Z][a-zA-Z0-9_]{0,1023}$/.test(value)) return '1-1024 chars, starts with letter, only letters, nums, underscores.';
        break;
      case 'bigQueryDataTableName':
        if (!/^[a-zA-Z_][a-zA-Z0-9_-]{0,1023}$/.test(value)) {
          return '1-1024 chars, starts with letter or underscore, can only contain letters, nums, underscores, or hyphens.';
        }
        if (['_table_', '_file_', '_partition_'].some(p => value.toLowerCase().startsWith(p))) {
          return 'Cannot start with _TABLE_, _FILE_, or _PARTITION_.';
        }
        break;
      default:
        break;
    }
    return '';
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    let errorMessage = validateField(name, value);
    switch (name) {
      case 'name': setName(value); break;
      case 'billingAccountId': setBillingAccountId(value); break;
      case 'bigQueryProjectId': setBigQueryProjectId(value); break;
      case 'bigQueryDatasetId': setBigQueryDatasetId(value); break;
      case 'bigQueryDataTableName': setBigQueryDataTableName(value); break;
      // LLM fields
      case 'projectId': setProjectId(value); break;
      case 'clickhouseUrl': setClickhouseUrl(value); break;
      case 'clickhouseUsername': setClickhouseUsername(value); break;
      case 'clickhousePassword': setClickhousePassword(value); break;
      default: break;
    }
    setFieldErrors(prev => ({ ...prev, [name]: errorMessage }));
  };

  const handleGoBack = () => navigate('/data-sources');
  const handleProviderChange = (event: React.ChangeEvent<HTMLInputElement>) => setSelectedProvider(event.target.value);

  // MODIFIED: Reworked submit logic for all providers
  const canSubmit = useMemo(() => {
    const hasNoErrors = Object.values(fieldErrors).every(error => error === '');
    const baseChecks = !!tenantId && hasNoErrors;

    if (selectedProvider === 'google-cloud-platform') {
        const gcpChecks =
            name.trim() !== '' &&
            billingAccountId.trim() !== '' &&
            bigQueryProjectId.trim() !== '' &&
            bigQueryDatasetId.trim() !== '' &&
            bigQueryDataTableName.trim() !== '' &&
            permissionsGranted &&
            !!serviceAccountEmail;
        return baseChecks && gcpChecks;
    }

    if (selectedProvider === 'llm') {
        if (useOwnLangfuse) { // Self-Hosted
            const requiredFields = name.trim() !== '' && projectId.trim() !== '';
            if (selfHostedConfigExists) {
                return baseChecks && requiredFields;
            }
            // First time self-hosted setup
            const clickhouseFields = clickhouseUrl.trim() !== '' && clickhouseUsername.trim() !== '' && clickhousePassword.trim() !== '';
            return baseChecks && requiredFields && clickhouseFields;
        } else { // Managed
            return baseChecks && name.trim() !== '';
        }
    }

    return false;
  }, [
    name, tenantId, billingAccountId, bigQueryProjectId, bigQueryDatasetId, bigQueryDataTableName,
    permissionsGranted, selectedProvider, serviceAccountEmail, fieldErrors, useOwnLangfuse,
    projectId, clickhouseUrl, clickhouseUsername, clickhousePassword, selfHostedConfigExists
  ]);

  const handleToastClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setIsToastOpen(false);
  };

  // MODIFIED: Reworked handleSubmit to build the correct mutation input
  const handleSubmit = async () => {
    if (!canSubmit) return;

    let input: any;

    if (selectedProvider === 'google-cloud-platform') {
        input = {
            tenantId,
            providerType: GQLProviderType.GCP,
            name: name.trim(),
            billingAccountId: billingAccountId.trim(),
            bigQueryProjectId: bigQueryProjectId.trim(),
            bigQueryDatasetId: bigQueryDatasetId.trim(),
            bigQueryDataTableName: bigQueryDataTableName.trim(),
        };
    } else if (selectedProvider === 'llm') {
        const configType = useOwnLangfuse ? 'SELF_HOSTED' : 'MANAGED';
        input = {
            tenantId,
            providerType: mapProviderToGqlType(selectedProvider),
            name: name.trim(),
            configType: configType,
        };
        if (configType === 'SELF_HOSTED') {
            input.projectId = projectId.trim();
            if (!selfHostedConfigExists) {
                input.clickhouseUrl = clickhouseUrl.trim();
                input.clickhouseUsername = clickhouseUsername.trim();
                input.clickhousePassword = clickhousePassword;
            }
        }
    } else {
        return;
    }

    try {
      if (selectedProvider === 'google-cloud-platform') {
        const validationResponse = await validateCredentials({ variables: { input } });
        const validationResult = validationResponse.data?.validateNewDataSourceCredentials;
        if (!validationResult?.isValid) {
          setToastMessage("We are unable to validate the GCP credentials. Please check them and try again.");
          setIsToastOpen(true);
          return;
        }
      }

      const createResponse = await createDataSource({ variables: { input } });
      if (createResponse.errors && createResponse.errors.length > 0) {
        throw new Error(createResponse.errors[0].message);
      }
      navigate('/data-sources', { state: { dataSourceAdded: true } });
    } catch (e: any) {
      console.error("Error creating data source:", e);
      setToastMessage("We were unable to add the Data Source. Please try again later.");
      setIsToastOpen(true);
    }
  };

  const isSubmitting = validateLoading || createLoading;

  if (!tenantId) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', p: 3 }}>
        <Alert severity="error">Required tenant information is missing.</Alert>
        <Typography sx={{mt: 2}}>This page must be accessed from the Data Sources list.</Typography>
        <Button onClick={handleGoBack} sx={{ mt: 2 }}>Go Back</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2.5 } }}>
      <Button
        onClick={() => setIsGoBackDialogOpen(true)}
        startIcon={<img src="/icons/back-arrow.svg" alt="Back" style={{ width: '12px', height: '12px', marginLeft:5, marginRight:3 }} />}
        sx={{ p: 0, textTransform: 'none', color: 'text.primary', '&:hover': { backgroundColor: 'transparent' } }}
      >
        <Typography sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '14px' }}>
          Add New Data Sources
        </Typography>
      </Button>
      <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '12px', color: 'rgba(102, 102, 102, 1)', mt: 0.5, marginLeft:2 }}>
        Add the Billing Account IDs and linked BigQuery dataset details associated with your organisation.
      </Typography>

      <Paper
        elevation={0}
        sx={{ height: { md: 'calc(100vh - 180px)' }, minHeight: '650px', borderRadius: '10px', backgroundColor: 'white', display: 'flex', flexDirection: 'column', overflow: 'hidden', mt: 2 }}
      >
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: theme.spacing(4), flexGrow: 1, overflow: 'auto' }}>
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: theme.spacing(3), padding: theme.spacing(3) }}>
            <Typography variant="h6" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '14px' }}>Select Provider</Typography>
            <RadioGroup row value={selectedProvider} onChange={handleProviderChange} name="select-provider-group" sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', marginLeft:'10px' }}>
              <FormControlLabel value="google-cloud-platform" control={<Radio sx={{ display: 'none' }} />} label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1, py: 1, borderRadius: '8px', border: `1px solid ${selectedProvider === 'google-cloud-platform' ? theme.palette.primary.main : theme.palette.grey[300]}`, backgroundColor: selectedProvider === 'google-cloud-platform' ? alpha(theme.palette.primary.main, 0.05) : theme.palette.grey[100], width: '255px', height: '35px', cursor: 'pointer' }}>
                  <img src="/icons/google-cloud-icon-2048x1646-7admxejz.svg" alt="GCP" style={{ height: 20, width: 20 }} />
                  <Typography sx={{ flexGrow: 1, fontSize: '12px' }}>Google Cloud Platform</Typography>
                  <Box sx={{ ml: 'auto', width: 16, height: 16, borderRadius: '50%', border: `1px solid ${selectedProvider === 'google-cloud-platform' ? theme.palette.primary.main : theme.palette.text.disabled}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, '&::before': { content: '""', width: 8, height: 8, borderRadius: '50%', backgroundColor: selectedProvider === 'google-cloud-platform' ? theme.palette.primary.main : 'transparent' } }} />
                </Box>
              }/>
              <FormControlLabel value="llm" control={<Radio sx={{ display: 'none' }} />} label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1, py: 1, borderRadius: '8px', border: `1px solid ${selectedProvider === 'llm' ? theme.palette.primary.main : theme.palette.grey[300]}`, backgroundColor: selectedProvider === 'llm' ? alpha(theme.palette.primary.main, 0.05) : theme.palette.grey[100], width: '255px', height: '35px', cursor: 'pointer' }}>
                  <img src="/icons/langfuse-icon.png" alt="LLM" style={{ height: 18, width: 18}} />
                  <Typography sx={{ flexGrow: 1, fontSize: '12px' }}>Langfuse</Typography>
                  <Box sx={{ ml: 'auto', width: 16, height: 16, borderRadius: '50%', border: `1px solid ${selectedProvider === 'llm' ? theme.palette.primary.main : theme.palette.text.disabled}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, '&::before': { content: '""', width: 8, height: 8, borderRadius: '50%', backgroundColor: selectedProvider === 'llm' ? theme.palette.primary.main : 'transparent' } }} />
                </Box>
              }/>
              <FormControlLabel value="aws" control={<Radio sx={{ display: 'none' }} />} disabled={true} label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1, py: 1, borderRadius: '8px', border: `1px solid ${theme.palette.grey[300]}`, backgroundColor: theme.palette.grey[100], width: '255px', height: '35px', cursor: 'not-allowed', opacity: 0.5 }}>
                  <img src="/icons/aws.png" alt="AWS" style={{ height: 20, width: 20 }} />
                  <Typography sx={{ flexGrow: 1, fontSize: '12px' }}>Amazon Web Services</Typography>
                  <Box sx={{ ml: 'auto', width: 16, height: 16, borderRadius: '50%', border: `1px solid ${theme.palette.text.disabled}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} />
                </Box>
              }/>
              <FormControlLabel value="azure" control={<Radio sx={{ display: 'none' }} />} disabled={true} label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1, py: 1, borderRadius: '8px', border: `1px solid ${theme.palette.grey[300]}`, backgroundColor: theme.palette.grey[100], width: '255px', height: '35px', cursor: 'not-allowed', opacity: 0.5 }}>
                  <img src="/icons/azure.svg" alt="Azure" style={{ height: 20, width: 20 }} />
                  <Typography sx={{ flexGrow: 1, fontSize: '12px' }}>Microsoft Azure</Typography>
                  <Box sx={{ ml: 'auto', width: 16, height: 16, borderRadius: '50%', border: `1px solid ${theme.palette.text.disabled}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} />
                </Box>
              }/>
            </RadioGroup>
            <Divider />
            
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '14px', color: ' #333333' }}>
                    {selectedProvider === 'llm' ? 'Langfuse Details' : 'Billing Account Details'}
                </Typography>
                {selectedProvider === 'llm' && (
                    <FormControlLabel
                        control={<Switch checked={useOwnLangfuse} onChange={(e) => setUseOwnLangfuse(e.target.checked)} />}
                        label="Use self hosted Langfuse"
                        labelPlacement="start"
                        sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '12px', fontWeight: 500, color:"#666666" } }}
                    />
                )}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0 }}>
                {selectedProvider === 'google-cloud-platform' && (
                    <>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1.5 }}>
                            <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="dataSourceName" sx={externalLabelStyle}>Data Source Name *</InputLabel><TextField autoFocus required fullWidth id="dataSourceName" name="name" size="small" placeholder="Enter data source name" sx={sxTextField} value={name} onChange={handleInputChange} error={!!fieldErrors.name} helperText={fieldErrors.name} InputProps={{ endAdornment: name && !fieldErrors.name && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} /></Box>
                            <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="billingAccountId" sx={externalLabelStyle}>Billing Account ID *</InputLabel><TextField required fullWidth id="billingAccountId" name="billingAccountId" size="small" placeholder="Enter GCP billing account ID" sx={sxTextField} value={billingAccountId} onChange={handleInputChange} error={!!fieldErrors.billingAccountId} helperText={fieldErrors.billingAccountId} InputProps={{ endAdornment: billingAccountId && !fieldErrors.billingAccountId && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} /></Box>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1.5 }}>
                            <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="bigQueryProjectId" sx={externalLabelStyle}>BigQuery Project ID *</InputLabel><TextField required fullWidth id="bigQueryProjectId" name="bigQueryProjectId" size="small" placeholder="Enter BigQuery project ID" sx={sxTextField} value={bigQueryProjectId} onChange={handleInputChange} error={!!fieldErrors.bigQueryProjectId} helperText={fieldErrors.bigQueryProjectId} InputProps={{ endAdornment: bigQueryProjectId && !fieldErrors.bigQueryProjectId && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} /></Box>
                            <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="bigQueryDatasetId" sx={externalLabelStyle}>BigQuery Dataset ID *</InputLabel><TextField required fullWidth id="bigQueryDatasetId" name="bigQueryDatasetId" size="small" placeholder="Enter BigQuery dataset ID" sx={sxTextField} value={bigQueryDatasetId} onChange={handleInputChange} error={!!fieldErrors.bigQueryDatasetId} helperText={fieldErrors.bigQueryDatasetId} InputProps={{ endAdornment: bigQueryDatasetId && !fieldErrors.bigQueryDatasetId && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} /></Box>
                        </Box>
                        <Box sx={{ mt: 1.5, width:'49%' }}><InputLabel htmlFor="bigQueryDataTableName" sx={externalLabelStyle}>BigQuery Table Name *</InputLabel><TextField required fullWidth id="bigQueryDataTableName" name="bigQueryDataTableName" size="small" placeholder="Enter BigQuery table name" sx={sxTextField} value={bigQueryDataTableName} onChange={handleInputChange} error={!!fieldErrors.bigQueryDataTableName} helperText={fieldErrors.bigQueryDataTableName} InputProps={{ endAdornment: bigQueryDataTableName && !fieldErrors.bigQueryDataTableName && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} /></Box>
                        <FormControlLabel control={<Checkbox checked={permissionsGranted} onChange={(e) => setPermissionsGranted(e.target.checked)}/>} label= {<Typography sx={{ fontFamily: 'Inter', fontSize: '14px', color: 'text.secondary' }}>I have granted the necessary permissions as described in the Setup Guide.</Typography>} disabled={!serviceAccountEmail || setupLoading}/>
                    </>
                )}
                
                {selectedProvider === 'llm' && !useOwnLangfuse && (
                    <Box sx={{ mt: -1, width: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
                        <InputLabel htmlFor="dataSourceName" sx={externalLabelStyle}>Data Source Name *</InputLabel>
                        <TextField autoFocus required fullWidth id="dataSourceName" name="name" size="small" placeholder="Enter your data source name" sx={sxTextField} value={name} onChange={handleInputChange} error={!!fieldErrors.name} helperText={fieldErrors.name} InputProps={{ endAdornment: name && !fieldErrors.name && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} />
                    </Box>
                )}

                {/* --- MODIFIED: Dynamic form for Self-Hosted LLM --- */}
                {selectedProvider === 'llm' && useOwnLangfuse && (
                  <>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1.5 }}>
                        <Box sx={{ flex: 1, flexDirection: 'column' }}>
                          <InputLabel htmlFor="dataSourceName" sx={externalLabelStyle}>Data Source Name *</InputLabel>
                          <TextField autoFocus required fullWidth id="dataSourceName" name="name" size="small" placeholder="Enter data source name" sx={sxTextField} value={name} onChange={handleInputChange} error={!!fieldErrors.name} helperText={fieldErrors.name} InputProps={{ endAdornment: name && !fieldErrors.name && (<InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}}/>
                        </Box>
                        <Box sx={{ flex: 1, flexDirection: 'column' }}>
                          <InputLabel htmlFor="projectId" sx={externalLabelStyle}>Project ID *</InputLabel>
                          <TextField required fullWidth id="projectId" name="projectId" size="small" placeholder="Enter your Langfuse Project ID" sx={sxTextField} value={projectId} onChange={handleInputChange} error={!!fieldErrors.projectId} helperText={fieldErrors.projectId} InputProps={{ endAdornment: projectId && !fieldErrors.projectId && (<InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}}/>
                        </Box>
                    </Box>

                    {llmConfigLoading ? (<CircularProgress sx={{ alignSelf: 'center', my: 4 }} />) : !selfHostedConfigExists && (
                      <>
                        <Typography sx={{ fontFamily: 'Inter', fontWeight: 500, fontSize: '13px', color: 'text.secondary', mt: 1 }}>
                          Please provide your ClickHouse credentials for the first setup.
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1.5 }}>
                          <Box sx={{ flex: 1, flexDirection: 'column' }}>
                            <InputLabel htmlFor="clickhouseUrl" sx={externalLabelStyle}>Clickhouse URL *</InputLabel>
                            <TextField required fullWidth id="clickhouseUrl" name="clickhouseUrl" size="small" placeholder="Enter Clickhouse URL" sx={sxTextField} value={clickhouseUrl} onChange={handleInputChange} />
                          </Box>
                           <Box sx={{ flex: 1, flexDirection: 'column' }}>
                            <InputLabel htmlFor="clickhouseUsername" sx={externalLabelStyle}>Clickhouse DB Username *</InputLabel>
                            <TextField required fullWidth id="clickhouseUsername" name="clickhouseUsername" size="small" placeholder="Enter Clickhouse DB Username" sx={sxTextField} value={clickhouseUsername} onChange={handleInputChange} />
                          </Box>
                        </Box>
                        <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)' }, mt: 1.5 }}>
                            <InputLabel htmlFor="clickhousePassword" sx={externalLabelStyle}>Clickhouse DB Password *</InputLabel>
                            <TextField required fullWidth id="clickhousePassword" name="clickhousePassword" type="password" size="small" placeholder="Enter Clickhouse DB Password" sx={sxTextField} value={clickhousePassword} onChange={handleInputChange} />
                        </Box>
                      </>
                    )}
                  </>
                )}
            </Box>
          </Box>
          
          <Box sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0, display: 'flex', flexDirection: 'column', backgroundColor:'rgba(243, 248, 254, 1)'}}>
              <Box sx={{ p: '15px', pb: 0, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',backgroundColor:'rgba(228, 240, 253, 1)', p: '12px', borderRadius: '4px' }}>
                    <Box sx={{ width :'250px', mr: 1 }}>
                        <Typography variant="h5" sx={{ fontSize:'12px',fontWeight: 600,mb:0.5 }}>Setup Guide</Typography>
                        <Typography variant="body2" sx={{ fontSize:'11px',fontWeight: 400 }}>Step-by-step guide to set up data access.</Typography>
                    </Box>
                    <img src="/icons/step-guide.svg" alt="Setup Guide" style={{ height: '70px', width: '70px', flexShrink: 0 }} />
                </Box>
              </Box>

              <Box sx={{ flexGrow: 1, overflowY: 'auto', p: '15px', pt: 2 }}>
                {selectedProvider === 'google-cloud-platform' && (
                  <>
                      {setupLoading && <CircularProgress size={20} sx={{alignSelf: 'center'}} />}
                      {gcpStepsData.map((item) => (
                          <Box sx={{ mb: 2 }} textAlign={'left'} key={item.step}>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main', textTransform: 'uppercase' }}>{item.step}</Typography>
                              <Typography sx={{ fontSize: '12px', fontWeight: 600, whiteSpace: 'pre-line' }}>{item.title}</Typography>
                              <Typography variant="body2" sx={{ fontSize: '11px', color: 'text.secondary', whiteSpace: 'pre-line', wordBreak: 'break-word' }}>{item.description}</Typography>
                          </Box>
                      ))}
                      {setupError && <Alert severity="error">Failed to get Service Account Email: {setupError.message}</Alert>}
                  </>
                )}
                {selectedProvider === 'llm' && <LlmSetupGuide />}
              </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, p: 2, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Button variant="text" href="https://cloud.google.com/billing/docs/how-to/find-billing-account-id" target="_blank" rel="noopener noreferrer" sx={{ color:'rgba(0, 100, 255, 1)', mr: 'auto', textTransform: 'none', textDecoration: 'none' }} startIcon={<img src="/linkurl.svg" alt="Help icon" width="16" height="16"/>}>
            Where do I find my Billing Account ID?
          </Button>
          <Button sx={{ backgroundColor:' #F2F7FF',textTransform: 'none', fontFamily:'Inter', borderRadius:'8px',borderColor: '#0064FF', color:'#0064FF'}} onClick={() => setIsGoBackDialogOpen(true)}>Cancel</Button>
          <Button
            variant="contained"
            sx={{ textTransform: 'none',fontFamily:'Inter', borderRadius:'8px', backgroundColor:' #0064FF',color:'white' }}
            onClick={handleSubmit}
            disabled={!canSubmit || isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isSubmitting ? (validateLoading ? 'Validating...' : 'Adding...') : 'Add Data Source'}
          </Button>
        </Box>
      </Paper>
      <Snackbar open={isToastOpen} autoHideDuration={6000} onClose={handleToastClose} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert icon={<CustomErrorIcon />} onClose={handleToastClose} sx={{ width: '100%', borderRadius: '50px', backgroundColor: 'white', boxShadow: theme.shadows[3], color: 'text.secondary', alignItems: 'center', p: theme.spacing(0.5, 2), '& .MuiAlert-message': { flex: 1, py: '12px' } }}>
          {toastMessage}
        </Alert>
      </Snackbar>
      <GoBackDialog open={isGoBackDialogOpen} onClose={() => setIsGoBackDialogOpen(false)} onConfirm={() => navigate('/data-sources')} />
    </Box>
  );
};

export default AddDataSourcesPage;


// import React, { useState, useMemo, useEffect } from 'react';
// import {
//   Box,
//   Typography,
//   Button,
//   TextField,
//   RadioGroup,
//   FormControlLabel,
//   Radio,
//   Paper,
//   Divider,
//   InputLabel,
//   CircularProgress,
//   Checkbox,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   Snackbar,
//   Alert,
//   Link,
//   InputAdornment,
// } from '@mui/material';
// import { useTheme } from '@mui/material/styles';
// import { alpha } from '@mui/material/styles';
// import { GitHub as GitHubIcon, Close as CloseIcon } from '@mui/icons-material';
// import { useNavigate, useOutletContext } from 'react-router-dom';
// import { gql, useLazyQuery, useMutation } from '@apollo/client';
// import type { Tenant } from '../../App';
// import { Check as CheckIcon } from '@mui/icons-material';


// // --- CHANGE 1: Updated GQLProviderType enum to use LLM ---
// export enum GQLProviderType {
//   GCP = "GCP",
//   LLM = "LLM",
//   AWS = "AWS",
//   AZURE = "Azure",
// }

// // --- Custom icon for the error toast ---
// const CustomErrorIcon = () => (
//     <Box
//       component="span"
//       sx={{
//         width: 24,
//         height: 24,
//         borderRadius: '50%',
//         display: 'flex',
//         alignItems: 'center',
//         justifyContent: 'center',
//         backgroundColor: '#D92D20',
//       }}
//     >
//       <CloseIcon sx={{ color: 'white', fontSize: '16px' }} />
//     </Box>
// );

// // --- GoBackDialog to confirm leaving the page ---
// const GoBackDialog = ({ open, onClose, onConfirm }: { open: boolean, onClose: () => void, onConfirm: () => void }) => {
//   if (!open) return null;

//   const backgroundTextureUrl = '/textures/dialog-background-texture.svg';
//   const goBackIconUrl = '/icons/go-back-dialog-icon.svg';

//   return (
//     <Dialog
//       open={open}
//       onClose={onClose}
//       PaperProps={{
//         sx: {
//           borderRadius: '24px',
//           width: '100%',
//           maxWidth: '520px',
//           p: 0.5,
//           position: 'relative',
//           overflow: 'hidden',
//           backgroundImage: `url(${backgroundTextureUrl})`,
//           backgroundRepeat: 'no-repeat',
//           backgroundPosition: 'top right',
//           backgroundColor: 'white',
//         },
//       }}
//     >
//       <DialogContent sx={{ p: 3, pt: 4, zIndex: 1 }}>
//         <Box
//           sx={{
//             mb: 3,
//             width: 50,
//             height: 50,
//             borderRadius: '12px',
//             display: 'flex',
//             alignItems: 'center',
//             justifyContent: 'center',
//             backgroundColor:' #E5EFFF'
//           }}
//         >
//           <img src={goBackIconUrl} alt="Go back icon" style={{ width: 19, height: 20 }} />
//         </Box>
//         <Typography sx={{ fontWeight: 600, mb: 1, fontSize: '16px', color: '#333333' }}>
//           Go Back
//         </Typography>
//         <Typography variant="body1" sx={{ color: ' #333333', fontSize: '14px', lineHeight: 1.6, fontWeight:400 }}>
//           Are you sure you want to leave this page? Leaving this page
//           will discard all the progress made in the below form.
//         </Typography>
//       </DialogContent>
//       <DialogActions sx={{ p: 3, justifyContent: 'flex-end', gap: 1.5, zIndex: 1 }}>
//         <Button
//           onClick={onClose}
//           sx={{
//             textTransform: 'none',
//             borderRadius: '8px',
//             backgroundColor: '#EFF6FF',
//             color: '#0064FF',
//             fontWeight: 500,
//             fontSize: '14px',
//             padding: '10px 20px',
//           }}
//         >
//           Cancel
//         </Button>
//         <Button
//           onClick={onConfirm}
//           size="large"
//           sx={{
//             textTransform: 'none',
//             borderRadius: '8px',
//             backgroundColor: '#0064FF',
//             fontWeight: 500,
//             fontSize: '14px',
//             padding: '10px 20px',
//             boxShadow: 'none',
//             color:'white'
//           }}
//         >
//           Yes, Go Back
//         </Button>
//       </DialogActions>
//     </Dialog>
//   );
// };

// // --- CHANGE 2: Updated the component title to LLM SDK Setup ---
// const LlmSetupGuide = () => {
//     const [activeManager, setActiveManager] = useState('npm');

//     const commands = {
//         npm: 'npm install langfuse\n\n# Node.js < 18\nnpm install langfuse-node',
//         pnpm: 'pnpm add langfuse\n\n# Node.js < 18\npnpm add langfuse-node',
//         yarn: 'yarn add langfuse\n\n# Node.js < 18\nyarn add langfuse-node',
//         bun: 'bun add langfuse\n\n# Node.js < 18\nbun add langfuse-node',
//     };

//     const Badge = ({ label, value, color, href }: { label: string, value: string, color: string, href?: string }) => (
//         <Button
//             component={href ? 'a' : 'button'}
//             href={href}
//             target="_blank"
//             rel="noopener noreferrer"
//             sx={{
//                 p: 0,
//                 m: 0,
//                 height: '20px',
//                 textTransform: 'none',
//                 borderRadius: '4px',
//                 overflow: 'hidden',
//                 '&:hover': {
//                     ...(href && {
//                         opacity: 0.8
//                     })
//                 }
//             }}>
//             <Box sx={{ bgcolor: '#555', color: 'white', px: 1, height: '100%', display: 'flex', alignItems: 'center' }}>
//                 {label === 'repo' && <GitHubIcon sx={{ fontSize: '14px', mr: 0.5 }} />}
//                 <Typography sx={{ fontSize: '10px', fontWeight: 500 }}>{label}</Typography>
//             </Box>
//             <Box sx={{ bgcolor: color, color: 'white', px: 1, height: '100%', display: 'flex', alignItems: 'center' }}>
//                 <Typography sx={{ fontSize: '10px', fontWeight: 500 }}>{value}</Typography>
//             </Box>
//         </Button>
//     );

//     const TabButton = ({ manager }: { manager: 'npm' | 'pnpm' | 'yarn' | 'bun' }) => (
//         <Button
//             onClick={() => setActiveManager(manager)}
//             sx={{
//                 p: '2px 8px',
//                 minWidth: 'auto',
//                 color: activeManager === manager ? 'primary.main' : 'text.secondary',
//                 fontWeight: 500,
//                 fontSize: '13px',
//                 borderBottom: 2,
//                 borderColor: activeManager === manager ? 'primary.main' : 'transparent',
//                 borderRadius: 0,
//                 textTransform: 'none'
//             }}
//         >
//             {manager}
//         </Button>
//     );

//     return (
//         <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
//           <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>LLM SDK Setup</Typography>
//           <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
//               The Langfuse SDKs are the recommended way to integrate with Langfuse.{' '}
//               <Link
//                 href="https://langfuse.com/integrations"
//                 target="_blank"
//                 rel="noopener noreferrer"
//                 sx={{ fontWeight: 500 }}
//               >
//                 [See a full list of Integration]
//               </Link>
//           </Typography>
//           <Divider sx={{ my: 1 }} />
//           <Typography sx={{ fontSize: '12px', color: '#333333', fontWeight:600 }}>
//               Install the Toolkit
//           </Typography>
//           <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
//               Pick your language and run one of these commands in your project's terminal.
//           </Typography>

//           <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>JS/TS</Typography>
//           <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
//               <Badge label="repo" value="langfuse-js" color="#007ec6" href="https://github.com/langfuse/langfuse-js" />
//               <Badge label="npm langfuse" value="v3.38.4" color="#007ec6" />
//               <Badge label="npm langfuse-node" value="v3.38.4" color="#007ec6" />
//           </Box>
//           <Box sx={{ display: 'flex', borderBottom: 1, borderColor: 'divider' }}>
//               <TabButton manager="npm" />
//               <TabButton manager="pnpm" />
//               <TabButton manager="yarn" />
//               <TabButton manager="bun" />
//           </Box>
//           <Box sx={{ bgcolor: '#f7f7f7', p: 1.5, borderRadius: '4px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', fontSize: '12px', border: '1px solid #e0e0e0' }}>
//               {commands[activeManager as keyof typeof commands]}
//           </Box>

//           <Divider sx={{ my: 2 }} />

//           <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>Python</Typography>
//           <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
//               <Badge label="repo" value="langfuse-python" color="#007ec6" href="https://github.com/langfuse/langfuse-python" />
//               <Badge label="pypi langfuse" value="v3.2.3" color="#007ec6" />
//           </Box>
//           <Box sx={{ bgcolor: '#f7f7f7', p: 1.5, borderRadius: '4px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', fontSize: '12px', border: '1px solid #e0e0e0', mt: 1 }}>
//               pip install langfuse
//           </Box>
//         </Box>
//     );
// };
// // --- END: Component ---

// // --- GraphQL Definitions ---
// const GET_TENANT_PROVIDER_ACCESS = gql`
//   query GetTenantProviderAccess($tenantId: ID!, $providerType: ProviderType!) {
//     getTenantProviderAccess(tenantId: $tenantId, providerType: $providerType) {
//       serviceAccountEmail
//     }
//   }
// `;

// const CREATE_DATA_SOURCE_MUTATION = gql`
//   mutation CreateDataSource($input: CreateDataSourceInput!) {
//     createDataSource(input: $input) {
//       __typename
//       ... on DataSource {
//         id
//         name
//       }
//       ... on LlmDataSource {
//         id
//         name
//       }
//     }
//   }
// `;

// const VALIDATE_NEW_DATA_SOURCE_CREDENTIALS_MUTATION = gql`
//   mutation ValidateNewDataSourceCredentials($input: ValidateNewDataSourceCredentialsInput!) {
//     validateNewDataSourceCredentials(input: $input) {
//       isValid
//       validationErrors
//     }
//   }
// `;

// // --- CHANGE 3: Updated mapping function to use 'llm' ---
// const mapProviderToGqlType = (frontendProviderValue: string): GQLProviderType => {
//   switch (frontendProviderValue) {
//     case 'google-cloud-platform': return GQLProviderType.GCP;
//     case 'llm': return GQLProviderType.LLM;
//     case 'aws': return GQLProviderType.AWS;
//     case 'azure': return GQLProviderType.AZURE;
//     default: throw new Error(`Unsupported provider value: ${frontendProviderValue}`);
//   }
// };

// interface AddDataSourceOutletContext {
//   currentTenant: Tenant | null;
// }

// const AddDataSourcesPage: React.FC = () => {
//   const theme = useTheme();
//   const navigate = useNavigate();
//   const { currentTenant } = useOutletContext<AddDataSourceOutletContext>();
//   const tenantId = currentTenant?.id;

//   // Form State
//   const [name, setName] = useState('');
//   const [billingAccountId, setBillingAccountId] = useState('');
//   const [bigQueryProjectId, setBigQueryProjectId] = useState('');
//   const [bigQueryDatasetId, setBigQueryDatasetId] = useState('');
//   const [bigQueryDataTableName, setBigQueryDataTableName] = useState('');
//   const [selectedProvider, setSelectedProvider] = useState('google-cloud-platform');
//   const [fieldErrors, setFieldErrors] = useState({ name: '', billingAccountId: '', bigQueryProjectId: '', bigQueryDatasetId: '', bigQueryDataTableName: '' });
//   const [permissionsGranted, setPermissionsGranted] = useState(false);

//   // Other component state
//   const [serviceAccountEmail, setServiceAccountEmail] = useState<string | null>(null);
//   const [isGoBackDialogOpen, setIsGoBackDialogOpen] = useState(false);
//   const [isToastOpen, setIsToastOpen] = useState(false);
//   const [toastMessage, setToastMessage] = useState('');

//   // Hooks for GraphQL operations
//   const [getTenantAccess, { loading: setupLoading, error: setupError }] = useLazyQuery(GET_TENANT_PROVIDER_ACCESS);
//   const [createDataSource, { loading: createLoading }] = useMutation(CREATE_DATA_SOURCE_MUTATION);
//   const [validateCredentials, { loading: validateLoading }] = useMutation(VALIDATE_NEW_DATA_SOURCE_CREDENTIALS_MUTATION);

//   useEffect(() => {
//     // Reset GCP-specific fields when provider changes
//     setBillingAccountId('');
//     setBigQueryProjectId('');
//     setBigQueryDatasetId('');
//     setBigQueryDataTableName('');
//     setPermissionsGranted(false);
//     setFieldErrors(prev => ({ name: prev.name, billingAccountId: '', bigQueryProjectId: '', bigQueryDatasetId: '', bigQueryDataTableName: '' }));

//     if (tenantId && selectedProvider === 'google-cloud-platform') {
//         getTenantAccess({
//             variables: { tenantId, providerType: GQLProviderType.GCP },
//             onCompleted: (data) => setServiceAccountEmail(data.getTenantProviderAccess?.serviceAccountEmail || null),
//             onError: (error) => console.error("Failed to fetch service account email:", error),
//         });
//     } else {
//         setServiceAccountEmail(null);
//     }
//   }, [selectedProvider, tenantId, getTenantAccess]);

//   const gcpStepsData = useMemo(() => {
//     return [
//         { step: 'STEP 1', title: 'Enable billing export to BigQuery', description: 'Go to Billing > Billing Export > Export to BigQuery > Choose Dataset' },
//         {
//           step: 'STEP 2',
//           title: 'Assign Roles to Service Account',
//           description: serviceAccountEmail
//             ? `Give the following roles:\n * BigQuery Data Viewer\n * BigQuery Job User\nTo: The service account (${serviceAccountEmail})`
//             : (setupLoading ? 'Fetching service account details...' : 'Could not retrieve service account.')
//         },
//         { step: 'STEP 3', title: 'Provide the following information below', description: 'Enter your GCP credentials and dataset details' },
//     ];
//   }, [serviceAccountEmail, setupLoading]);

//   const externalLabelStyle = { fontFamily: 'Inter, sans-serif', fontSize: '12px', fontWeight: 400, color: theme.palette.text.primary, marginBottom: theme.spacing(0.75), display: 'block' };
//   const sxTextField = {
//     '& .MuiOutlinedInput-root': {
//       '& fieldset': { borderRadius: '8px' },
//       '&.Mui-focused fieldset': { borderColor: 'rgba(0, 0, 0, 0.23)' },
//     },
//     '& .MuiInputBase-input': {
//       fontFamily: 'Inter, sans-serif', color: theme.palette.text.primary, fontSize: '12px',
//       '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
//         WebkitBoxShadow: '0 0 0 30px white inset !important', WebkitTextFillColor: `${theme.palette.text.primary} !important`,
//         transition: 'background-color 5000s ease-in-out 0s',
//       },
//     },
//     '& .MuiInputBase-input::placeholder': {
//       fontFamily: 'Inter, sans-serif', color: 'rgba(185, 185, 185, 1)', fontSize: '12px', opacity: 1,
//     },
//   };

//   // --- CHANGE 4: Updated validation logic to check for 'llm' ---
//   const validateField = (name: string, value: string): string => {
//     if (selectedProvider === 'llm' && name !== 'name') {
//         return '';
//     }
//     switch (name) {
//       case 'name':
//         if (!/^[a-z0-9\s]{2,50}$/.test(value)) return 'Must be 2-50 chars, lowercase letters, numbers, spaces.';
//         break;
//       case 'billingAccountId':
//         if (!/^[0-9A-F]{6}-[0-9A-F]{6}-[0-9A-F]{6}$/i.test(value)) return 'Format: XXXXXX-XXXXXX-XXXXXX (hex digits).';
//         break;
//       case 'bigQueryProjectId':
//         if (!/^[a-z][a-z0-9-]{4,28}[a-z0-9]$/.test(value)) return '6-30 chars, starts with letter, ends with letter/num, only lowercase, nums, hyphens.';
//         break;
//       case 'bigQueryDatasetId':
//         if (!/^[a-zA-Z][a-zA-Z0-9_]{0,1023}$/.test(value)) return '1-1024 chars, starts with letter, only letters, nums, underscores.';
//         break;
//       case 'bigQueryDataTableName':
//         if (!/^[a-zA-Z_][a-zA-Z0-9_-]{0,1023}$/.test(value)) {
//           return '1-1024 chars, starts with letter or underscore, can only contain letters, nums, underscores, or hyphens.';
//         }
//         if (['_table_', '_file_', '_partition_'].some(p => value.toLowerCase().startsWith(p))) {
//           return 'Cannot start with _TABLE_, _FILE_, or _PARTITION_.';
//         }
//         break;
//       default:
//         break;
//     }
//     return '';
//   };

//   const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
//     const { name, value } = event.target;
//     let errorMessage = validateField(name, value);
//     switch (name) {
//       case 'name': setName(value); break;
//       case 'billingAccountId': setBillingAccountId(value); break;
//       case 'bigQueryProjectId': setBigQueryProjectId(value); break;
//       case 'bigQueryDatasetId': setBigQueryDatasetId(value); break;
//       case 'bigQueryDataTableName': setBigQueryDataTableName(value); break;
//       default: break;
//     }
//     setFieldErrors(prev => ({ ...prev, [name]: errorMessage }));
//   };

//   const handleGoBack = () => navigate('/data-sources');
//   const handleProviderChange = (event: React.ChangeEvent<HTMLInputElement>) => setSelectedProvider(event.target.value);

//   // --- CHANGE 5: Updated submission logic to check for 'llm' ---
//   const canSubmit = useMemo(() => {
//     const hasNoErrors = Object.values(fieldErrors).every(error => error === '');
//     const baseChecks = name.trim() !== '' && !!tenantId && hasNoErrors;
//     if (selectedProvider === 'llm') {
//         return baseChecks;
//     }
//     if (selectedProvider === 'google-cloud-platform') {
//         const gcpChecks =
//             billingAccountId.trim() !== '' &&
//             bigQueryProjectId.trim() !== '' &&
//             bigQueryDatasetId.trim() !== '' &&
//             bigQueryDataTableName.trim() !== '' &&
//             permissionsGranted &&
//             !!serviceAccountEmail;
//         return baseChecks && gcpChecks;
//     }
//     return false;
//   }, [
//     name, tenantId, billingAccountId, bigQueryProjectId, bigQueryDatasetId, bigQueryDataTableName,
//     permissionsGranted, selectedProvider, serviceAccountEmail, fieldErrors
//   ]);

//   const handleToastClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
//     if (reason === 'clickaway') return;
//     setIsToastOpen(false);
//   };

//   const handleSubmit = async () => {
//     if (!canSubmit) return;

//     let input: any = {
//       name: name.trim(),
//       tenantId,
//       providerType: mapProviderToGqlType(selectedProvider),
//     };

//     if (selectedProvider === 'google-cloud-platform') {
//         input = {
//             ...input,
//             billingAccountId: billingAccountId.trim(),
//             bigQueryProjectId: bigQueryProjectId.trim(),
//             bigQueryDatasetId: bigQueryDatasetId.trim(),
//             bigQueryDataTableName: bigQueryDataTableName.trim(),
//         };
//     }
//     // No extra fields needed for 'llm' provider

//     try {
//       if (selectedProvider === 'google-cloud-platform') {
//         const validationResponse = await validateCredentials({ variables: { input } });
//         const validationResult = validationResponse.data?.validateNewDataSourceCredentials;
//         if (!validationResult?.isValid) {
//           setToastMessage("We are unable to validate the GCP credentials. Please check them and try again.");
//           setIsToastOpen(true);
//           return;
//         }
//       }

//       const createResponse = await createDataSource({ variables: { input } });
//       if (createResponse.errors && createResponse.errors.length > 0) {
//         throw new Error(createResponse.errors[0].message);
//       }
//       navigate('/data-sources', { state: { dataSourceAdded: true } });
//     } catch (e: any) {
//       setToastMessage("We were unable to add the Data Source. Please try again later.");
//       setIsToastOpen(true);
//     }
//   };

//   const isSubmitting = validateLoading || createLoading;

//   if (!tenantId) {
//     return (
//       <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', p: 3 }}>
//         <Alert severity="error">Required tenant information is missing.</Alert>
//         <Typography sx={{mt: 2}}>This page must be accessed from the Data Sources list.</Typography>
//         <Button onClick={handleGoBack} sx={{ mt: 2 }}>Go Back</Button>
//       </Box>
//     );
//   }

//   return (
//     <Box sx={{ p: { xs: 1, sm: 2.5 } }}>
//       <Button
//         onClick={() => setIsGoBackDialogOpen(true)}
//         startIcon={<img src="/icons/back-arrow.svg" alt="Back" style={{ width: '12px', height: '12px', marginLeft:5, marginRight:3 }} />}
//         sx={{ p: 0, textTransform: 'none', color: 'text.primary', '&:hover': { backgroundColor: 'transparent' } }}
//       >
//         <Typography sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '14px' }}>
//           Add New Data Sources
//         </Typography>
//       </Button>
//       <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '12px', color: 'rgba(102, 102, 102, 1)', mt: 0.5, marginLeft:2 }}>
//         Add the Billing Account IDs and linked BigQuery dataset details associated with your organisation.
//       </Typography>

//       <Paper
//         elevation={0}
//         sx={{ height: { md: 'calc(100vh - 180px)' }, minHeight: '650px', borderRadius: '10px', backgroundColor: 'white', display: 'flex', flexDirection: 'column', overflow: 'hidden', mt: 2 }}
//       >
//         <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: theme.spacing(4), flexGrow: 1, overflow: 'auto' }}>
//           <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: theme.spacing(3), padding: theme.spacing(3) }}>
//             <Typography variant="h6" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '14px' }}>Select Provider</Typography>
//             <RadioGroup row value={selectedProvider} onChange={handleProviderChange} name="select-provider-group" sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', marginLeft:'10px' }}>
//               <FormControlLabel value="google-cloud-platform" control={<Radio sx={{ display: 'none' }} />} label={
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1, py: 1, borderRadius: '8px', border: `1px solid ${selectedProvider === 'google-cloud-platform' ? theme.palette.primary.main : theme.palette.grey[300]}`, backgroundColor: selectedProvider === 'google-cloud-platform' ? alpha(theme.palette.primary.main, 0.05) : theme.palette.grey[100], width: '255px', height: '35px', cursor: 'pointer' }}>
//                   <img src="/icons/google-cloud-icon-2048x1646-7admxejz.svg" alt="GCP" style={{ height: 20, width: 20 }} />
//                   <Typography sx={{ flexGrow: 1, fontSize: '12px' }}>Google Cloud Platform</Typography>
//                   <Box sx={{ ml: 'auto', width: 16, height: 16, borderRadius: '50%', border: `1px solid ${selectedProvider === 'google-cloud-platform' ? theme.palette.primary.main : theme.palette.text.disabled}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, '&::before': { content: '""', width: 8, height: 8, borderRadius: '50%', backgroundColor: selectedProvider === 'google-cloud-platform' ? theme.palette.primary.main : 'transparent' } }} />
//                 </Box>
//               }/>
//               {/* --- CHANGE 6: Updated the radio button to use 'llm' --- */}
//               <FormControlLabel value="llm" control={<Radio sx={{ display: 'none' }} />} label={
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1, py: 1, borderRadius: '8px', border: `1px solid ${selectedProvider === 'llm' ? theme.palette.primary.main : theme.palette.grey[300]}`, backgroundColor: selectedProvider === 'llm' ? alpha(theme.palette.primary.main, 0.05) : theme.palette.grey[100], width: '255px', height: '35px', cursor: 'pointer' }}>
//                   <img src="/icons/llm-observability-blue.svg" alt="LLM" style={{ height: 18, width: 18}} />
//                   <Typography sx={{ flexGrow: 1, fontSize: '12px' }}>LLM</Typography>
//                   <Box sx={{ ml: 'auto', width: 16, height: 16, borderRadius: '50%', border: `1px solid ${selectedProvider === 'llm' ? theme.palette.primary.main : theme.palette.text.disabled}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, '&::before': { content: '""', width: 8, height: 8, borderRadius: '50%', backgroundColor: selectedProvider === 'llm' ? theme.palette.primary.main : 'transparent' } }} />
//                 </Box>
//               }/>
//               <FormControlLabel value="aws" control={<Radio sx={{ display: 'none' }} />} disabled={true} label={
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1, py: 1, borderRadius: '8px', border: `1px solid ${theme.palette.grey[300]}`, backgroundColor: theme.palette.grey[100], width: '255px', height: '35px', cursor: 'not-allowed', opacity: 0.5 }}>
//                   <img src="/icons/aws.png" alt="AWS" style={{ height: 20, width: 20 }} />
//                   <Typography sx={{ flexGrow: 1, fontSize: '12px' }}>Amazon Web Services</Typography>
//                   <Box sx={{ ml: 'auto', width: 16, height: 16, borderRadius: '50%', border: `1px solid ${theme.palette.text.disabled}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} />
//                 </Box>
//               }/>
//               <FormControlLabel value="azure" control={<Radio sx={{ display: 'none' }} />} disabled={true} label={
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1, py: 1, borderRadius: '8px', border: `1px solid ${theme.palette.grey[300]}`, backgroundColor: theme.palette.grey[100], width: '255px', height: '35px', cursor: 'not-allowed', opacity: 0.5 }}>
//                   <img src="/icons/azure.svg" alt="Azure" style={{ height: 20, width: 20 }} />
//                   <Typography sx={{ flexGrow: 1, fontSize: '12px' }}>Microsoft Azure</Typography>
//                   <Box sx={{ ml: 'auto', width: 16, height: 16, borderRadius: '50%', border: `1px solid ${theme.palette.text.disabled}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} />
//                 </Box>
//               }/>
//             </RadioGroup>
//             <Divider />
//             {/* --- CHANGE 7: Updated the section title to 'LLM Details' --- */}
//             <Typography sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '14px',color:' #333333' }}>
//                 {selectedProvider === 'llm' ? 'LLM Details' : 'Billing Account Details'}
//             </Typography>

//             <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0 }}>
//                 {selectedProvider === 'google-cloud-platform' && (
//                     <>
//                         <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1.5 }}>
//                             <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="dataSourceName" sx={externalLabelStyle}>Data Source Name *</InputLabel><TextField autoFocus required fullWidth id="dataSourceName" name="name" size="small" placeholder="Enter data source name" sx={sxTextField} value={name} onChange={handleInputChange} error={!!fieldErrors.name} helperText={fieldErrors.name} InputProps={{ endAdornment: name && !fieldErrors.name && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} /></Box>
//                             <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="billingAccountId" sx={externalLabelStyle}>Billing Account ID *</InputLabel><TextField required fullWidth id="billingAccountId" name="billingAccountId" size="small" placeholder="Enter GCP billing account ID" sx={sxTextField} value={billingAccountId} onChange={handleInputChange} error={!!fieldErrors.billingAccountId} helperText={fieldErrors.billingAccountId} InputProps={{ endAdornment: billingAccountId && !fieldErrors.billingAccountId && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} /></Box>
//                         </Box>
//                         <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1.5 }}>
//                             <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="bigQueryProjectId" sx={externalLabelStyle}>BigQuery Project ID *</InputLabel><TextField required fullWidth id="bigQueryProjectId" name="bigQueryProjectId" size="small" placeholder="Enter BigQuery project ID" sx={sxTextField} value={bigQueryProjectId} onChange={handleInputChange} error={!!fieldErrors.bigQueryProjectId} helperText={fieldErrors.bigQueryProjectId} InputProps={{ endAdornment: bigQueryProjectId && !fieldErrors.bigQueryProjectId && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} /></Box>
//                             <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="bigQueryDatasetId" sx={externalLabelStyle}>BigQuery Dataset ID *</InputLabel><TextField required fullWidth id="bigQueryDatasetId" name="bigQueryDatasetId" size="small" placeholder="Enter BigQuery dataset ID" sx={sxTextField} value={bigQueryDatasetId} onChange={handleInputChange} error={!!fieldErrors.bigQueryDatasetId} helperText={fieldErrors.bigQueryDatasetId} InputProps={{ endAdornment: bigQueryDatasetId && !fieldErrors.bigQueryDatasetId && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} /></Box>
//                         </Box>
//                         <Box sx={{ mt: 1.5, width:'49%' }}><InputLabel htmlFor="bigQueryDataTableName" sx={externalLabelStyle}>BigQuery Table Name *</InputLabel><TextField required fullWidth id="bigQueryDataTableName" name="bigQueryDataTableName" size="small" placeholder="Enter BigQuery table name" sx={sxTextField} value={bigQueryDataTableName} onChange={handleInputChange} error={!!fieldErrors.bigQueryDataTableName} helperText={fieldErrors.bigQueryDataTableName} InputProps={{ endAdornment: bigQueryDataTableName && !fieldErrors.bigQueryDataTableName && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} /></Box>
//                         <FormControlLabel control={<Checkbox checked={permissionsGranted} onChange={(e) => setPermissionsGranted(e.target.checked)}/>} label= {<Typography sx={{ fontFamily: 'Inter', fontSize: '14px', color: 'text.secondary' }}>I have granted the necessary permissions as described in the Setup Guide.</Typography>} disabled={!serviceAccountEmail || setupLoading}/>
//                     </>
//                 )}
//                 {/* --- CHANGE 8: Updated conditional rendering to check for 'llm' --- */}
//                 {selectedProvider === 'llm' && (
//                     <Box sx={{ mt: 1.5, width: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
//                         <InputLabel htmlFor="dataSourceName" sx={externalLabelStyle}>Data Source Name *</InputLabel>
//                         <TextField autoFocus required fullWidth id="dataSourceName" name="name" size="small" placeholder="Enter your data source name" sx={sxTextField} value={name} onChange={handleInputChange} error={!!fieldErrors.name} helperText={fieldErrors.name} InputProps={{ endAdornment: name && !fieldErrors.name && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} />
//                     </Box>
//                 )}
//             </Box>
//           </Box>
          
//           <Box sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0, display: 'flex', flexDirection: 'column', backgroundColor:'rgba(243, 248, 254, 1)'}}>
//               <Box sx={{ p: '15px', pb: 0, flexShrink: 0 }}>
//                 <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',backgroundColor:'rgba(228, 240, 253, 1)', p: '12px', borderRadius: '4px' }}>
//                     <Box sx={{ width :'250px', mr: 1 }}>
//                         <Typography variant="h5" sx={{ fontSize:'12px',fontWeight: 600,mb:0.5 }}>Setup Guide</Typography>
//                         <Typography variant="body2" sx={{ fontSize:'11px',fontWeight: 400 }}>Step-by-step guide to set up data access.</Typography>
//                     </Box>
//                     <img src="/icons/step-guide.svg" alt="Setup Guide" style={{ height: '70px', width: '70px', flexShrink: 0 }} />
//                 </Box>
//               </Box>

//               <Box sx={{ flexGrow: 1, overflowY: 'auto', p: '15px', pt: 2 }}>
//                 {selectedProvider === 'google-cloud-platform' && (
//                   <>
//                       {setupLoading && <CircularProgress size={20} sx={{alignSelf: 'center'}} />}
//                       {gcpStepsData.map((item) => (
//                           <Box sx={{ mb: 2 }} textAlign={'left'} key={item.step}>
//                               <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main', textTransform: 'uppercase' }}>{item.step}</Typography>
//                               <Typography sx={{ fontSize: '12px', fontWeight: 600, whiteSpace: 'pre-line' }}>{item.title}</Typography>
//                               <Typography variant="body2" sx={{ fontSize: '11px', color: 'text.secondary', whiteSpace: 'pre-line', wordBreak: 'break-word' }}>{item.description}</Typography>
//                           </Box>
//                       ))}
//                       {setupError && <Alert severity="error">Failed to get Service Account Email: {setupError.message}</Alert>}
//                   </>
//                 )}
//                 {/* --- CHANGE 9: Updated conditional rendering to check for 'llm' --- */}
//                 {selectedProvider === 'llm' && <LlmSetupGuide />}
//               </Box>
//           </Box>
//         </Box>
//         <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, p: 2, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
//           <Button variant="text" href="https://cloud.google.com/billing/docs/how-to/find-billing-account-id" target="_blank" rel="noopener noreferrer" sx={{ color:'rgba(0, 100, 255, 1)', mr: 'auto', textTransform: 'none', textDecoration: 'none' }} startIcon={<img src="/linkurl.svg" alt="Help icon" width="16" height="16"/>}>
//             Where do I find my Billing Account ID?
//           </Button>
//           <Button sx={{ backgroundColor:' #F2F7FF',textTransform: 'none', fontFamily:'Inter', borderRadius:'8px',borderColor: '#0064FF', color:'#0064FF'}} onClick={() => setIsGoBackDialogOpen(true)}>Cancel</Button>
//           <Button
//             variant="contained"
//             sx={{ textTransform: 'none',fontFamily:'Inter', borderRadius:'8px', backgroundColor:' #0064FF',color:'white' }}
//             onClick={handleSubmit}
//             disabled={!canSubmit || isSubmitting}
//             startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
//           >
//             {isSubmitting ? (validateLoading ? 'Validating...' : 'Adding...') : 'Add Data Source'}
//           </Button>
//         </Box>
//       </Paper>
//       <Snackbar open={isToastOpen} autoHideDuration={6000} onClose={handleToastClose} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
//         <Alert icon={<CustomErrorIcon />} onClose={handleToastClose} sx={{ width: '100%', borderRadius: '50px', backgroundColor: 'white', boxShadow: theme.shadows[3], color: 'text.secondary', alignItems: 'center', p: theme.spacing(0.5, 2), '& .MuiAlert-message': { flex: 1, py: '12px' } }}>
//           {toastMessage}
//         </Alert>
//       </Snackbar>
//       <GoBackDialog open={isGoBackDialogOpen} onClose={() => setIsGoBackDialogOpen(false)} onConfirm={() => navigate('/data-sources')} />
//     </Box>
//   );
// };

// export default AddDataSourcesPage;








// import React, { useState, useMemo, useEffect } from 'react';
// import {
//   Box,
//   Typography,
//   Button,
//   TextField,
//   RadioGroup,
//   FormControlLabel,
//   Radio,
//   Paper,
//   Divider,
//   InputLabel,
//   CircularProgress,
//   Checkbox,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   Snackbar,
//   Alert,
//   Link,
//   InputAdornment,
//   Switch,
// } from '@mui/material';
// import { useTheme } from '@mui/material/styles';
// import { alpha } from '@mui/material/styles';
// import { GitHub as GitHubIcon, Close as CloseIcon } from '@mui/icons-material';
// import { useNavigate, useOutletContext, useLocation } from 'react-router-dom';
// import { gql, useLazyQuery, useMutation } from '@apollo/client';
// import type { Tenant } from '../../App';
// import { Check as CheckIcon } from '@mui/icons-material';


// export enum GQLProviderType {
//   GCP = "GCP",
//   LANGFUSE = "LANGFUSE",
//   AWS = "AWS",
//   AZURE = "Azure",
// }

// const CustomErrorIcon = () => (
//     <Box
//       component="span"
//       sx={{
//         width: 24,
//         height: 24,
//         borderRadius: '50%',
//         display: 'flex',
//         alignItems: 'center',
//         justifyContent: 'center',
//         backgroundColor: '#D92D20',
//       }}
//     >
//       <CloseIcon sx={{ color: 'white', fontSize: '16px' }} />
//     </Box>
// );

// const GoBackDialog = ({ open, onClose, onConfirm }: { open: boolean, onClose: () => void, onConfirm: () => void }) => {
//   if (!open) return null;

//   const backgroundTextureUrl = '/textures/dialog-background-texture.svg';
//   const goBackIconUrl = '/icons/go-back-dialog-icon.svg';

//   return (
//     <Dialog
//       open={open}
//       onClose={onClose}
//       PaperProps={{
//         sx: {
//           borderRadius: '24px',
//           width: '100%',
//           maxWidth: '520px',
//           p: 0.5,
//           position: 'relative',
//           overflow: 'hidden',
//           backgroundImage: `url(${backgroundTextureUrl})`,
//           backgroundRepeat: 'no-repeat',
//           backgroundPosition: 'top right',
//           backgroundColor: 'white',
//         },
//       }}
//     >
//       <DialogContent sx={{ p: 3, pt: 4, zIndex: 1 }}>
//         <Box
//           sx={{
//             mb: 3,
//             width: 50,
//             height: 50,
//             borderRadius: '12px',
//             display: 'flex',
//             alignItems: 'center',
//             justifyContent: 'center',
//             backgroundColor:' #E5EFFF'
//           }}
//         >
//           <img src={goBackIconUrl} alt="Go back icon" style={{ width: 19, height: 20 }} />
//         </Box>
//         <Typography sx={{ fontWeight: 600, mb: 1, fontSize: '16px', color: '#333333' }}>
//           Go Back
//         </Typography>
//         <Typography variant="body1" sx={{ color: ' #333333', fontSize: '14px', lineHeight: 1.6, fontWeight:400 }}>
//           Are you sure you want to leave this page? Leaving this page
//           will discard all the progress made in the below form.
//         </Typography>
//       </DialogContent>
//       <DialogActions sx={{ p: 3, justifyContent: 'flex-end', gap: 1.5, zIndex: 1 }}>
//         <Button
//           onClick={onClose}
//           sx={{
//             textTransform: 'none',
//             borderRadius: '8px',
//             backgroundColor: '#EFF6FF',
//             color: '#0064FF',
//             fontWeight: 500,
//             fontSize: '14px',
//             padding: '10px 20px',
//           }}
//         >
//           Cancel
//         </Button>
//         <Button
//           onClick={onConfirm}
//           size="large"
//           sx={{
//             textTransform: 'none',
//             borderRadius: '8px',
//             backgroundColor: '#0064FF',
//             fontWeight: 500,
//             fontSize: '14px',
//             padding: '10px 20px',
//             boxShadow: 'none',
//             color:'white'
//           }}
//         >
//           Yes, Go Back
//         </Button>
//       </DialogActions>
//     </Dialog>
//   );
// };

// const LlmSetupGuide = () => {
//     const [activeManager, setActiveManager] = useState('npm');

//     const commands = {
//         npm: 'npm install langfuse\n\n# Node.js < 18\nnpm install langfuse-node',
//         pnpm: 'pnpm add langfuse\n\n# Node.js < 18\npnpm add langfuse-node',
//         yarn: 'yarn add langfuse\n\n# Node.js < 18\nyarn add langfuse-node',
//         bun: 'bun add langfuse\n\n# Node.js < 18\nbun add langfuse-node',
//     };

//     const Badge = ({ label, value, color, href }: { label: string, value: string, color: string, href?: string }) => (
//         <Button
//             component={href ? 'a' : 'button'}
//             href={href}
//             target="_blank"
//             rel="noopener noreferrer"
//             sx={{
//                 p: 0,
//                 m: 0,
//                 height: '20px',
//                 textTransform: 'none',
//                 borderRadius: '4px',
//                 overflow: 'hidden',
//                 '&:hover': {
//                     ...(href && {
//                         opacity: 0.8
//                     })
//                 }
//             }}>
//             <Box sx={{ bgcolor: '#555', color: 'white', px: 1, height: '100%', display: 'flex', alignItems: 'center' }}>
//                 {label === 'repo' && <GitHubIcon sx={{ fontSize: '14px', mr: 0.5 }} />}
//                 <Typography sx={{ fontSize: '10px', fontWeight: 500 }}>{label}</Typography>
//             </Box>
//             <Box sx={{ bgcolor: color, color: 'white', px: 1, height: '100%', display: 'flex', alignItems: 'center' }}>
//                 <Typography sx={{ fontSize: '10px', fontWeight: 500 }}>{value}</Typography>
//             </Box>
//         </Button>
//     );

//     const TabButton = ({ manager }: { manager: 'npm' | 'pnpm' | 'yarn' | 'bun' }) => (
//         <Button
//             onClick={() => setActiveManager(manager)}
//             sx={{
//                 p: '2px 8px',
//                 minWidth: 'auto',
//                 color: activeManager === manager ? 'primary.main' : 'text.secondary',
//                 fontWeight: 500,
//                 fontSize: '13px',
//                 borderBottom: 2,
//                 borderColor: activeManager === manager ? 'primary.main' : 'transparent',
//                 borderRadius: 0,
//                 textTransform: 'none'
//             }}
//         >
//             {manager}
//         </Button>
//     );

//     return (
//         <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
//           <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>LLM SDK Setup</Typography>
//           <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
//               The Langfuse SDKs are the recommended way to integrate with Langfuse.{' '}
//               <Link
//                 href="https://langfuse.com/integrations"
//                 target="_blank"
//                 rel="noopener noreferrer"
//                 sx={{ fontWeight: 500 }}
//               >
//                 [See a full list of Integration]
//               </Link>
//           </Typography>
//           <Divider sx={{ my: 1 }} />
//           <Typography sx={{ fontSize: '12px', color: '#333333', fontWeight:600 }}>
//               Install the Toolkit
//           </Typography>
//           <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
//               Pick your language and run one of these commands in your project's terminal.
//           </Typography>

//           <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>JS/TS</Typography>
//           <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
//               <Badge label="repo" value="langfuse-js" color="#007ec6" href="https://github.com/langfuse/langfuse-js" />
//               <Badge label="npm langfuse" value="v3.38.4" color="#007ec6" />
//               <Badge label="npm langfuse-node" value="v3.38.4" color="#007ec6" />
//           </Box>
//           <Box sx={{ display: 'flex', borderBottom: 1, borderColor: 'divider' }}>
//               <TabButton manager="npm" />
//               <TabButton manager="pnpm" />
//               <TabButton manager="yarn" />
//               <TabButton manager="bun" />
//           </Box>
//           <Box sx={{ bgcolor: '#f7f7f7', p: 1.5, borderRadius: '4px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', fontSize: '12px', border: '1px solid #e0e0e0' }}>
//               {commands[activeManager as keyof typeof commands]}
//           </Box>

//           <Divider sx={{ my: 2 }} />

//           <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>Python</Typography>
//           <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
//               <Badge label="repo" value="langfuse-python" color="#007ec6" href="https://github.com/langfuse/langfuse-python" />
//               <Badge label="pypi langfuse" value="v3.2.3" color="#007ec6" />
//           </Box>
//           <Box sx={{ bgcolor: '#f7f7f7', p: 1.5, borderRadius: '4px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', fontSize: '12px', border: '1px solid #e0e0e0', mt: 1 }}>
//               pip install langfuse
//           </Box>
//         </Box>
//     );
// };

// const GET_TENANT_PROVIDER_ACCESS = gql`
//   query GetTenantProviderAccess($tenantId: ID!, $providerType: ProviderType!) {
//     getTenantProviderAccess(tenantId: $tenantId, providerType: $providerType) {
//       serviceAccountEmail
//     }
//   }
// `;

// const CREATE_DATA_SOURCE_MUTATION = gql`
//   mutation CreateDataSource($input: CreateDataSourceInput!) {
//     createDataSource(input: $input) {
//       __typename
//       ... on DataSource {
//         id
//         name
//       }
//       ... on LlmDataSource {
//         id
//         name
//       }
//     }
//   }
// `;

// const VALIDATE_NEW_DATA_SOURCE_CREDENTIALS_MUTATION = gql`
//   mutation ValidateNewDataSourceCredentials($input: ValidateNewDataSourceCredentialsInput!) {
//     validateNewDataSourceCredentials(input: $input) {
//       isValid
//       validationErrors
//     }
//   }
// `;

// const mapProviderToGqlType = (frontendProviderValue: string): GQLProviderType => {
//   switch (frontendProviderValue) {
//     case 'google-cloud-platform': return GQLProviderType.GCP;
//     case 'llm': return GQLProviderType.LANGFUSE;
//     case 'aws': return GQLProviderType.AWS;
//     case 'azure': return GQLProviderType.AZURE;
//     default: throw new Error(`Unsupported provider value: ${frontendProviderValue}`);
//   }
// };

// interface AddDataSourceOutletContext {
//   currentTenant: Tenant | null;
// }

// const AddDataSourcesPage: React.FC = () => {
//   const theme = useTheme();
//   const navigate = useNavigate();
//   const location = useLocation();
//   const { currentTenant } = useOutletContext<AddDataSourceOutletContext>();
//   const tenantId = currentTenant?.id;

//   const [name, setName] = useState('');
//   const [billingAccountId, setBillingAccountId] = useState('');
//   const [bigQueryProjectId, setBigQueryProjectId] = useState('');
//   const [bigQueryDatasetId, setBigQueryDatasetId] = useState('');
//   const [bigQueryDataTableName, setBigQueryDataTableName] = useState('');
//   const [selectedProvider, setSelectedProvider] = useState(location.state?.defaultProvider || 'google-cloud-platform');
//   const [fieldErrors, setFieldErrors] = useState({ name: '', billingAccountId: '', bigQueryProjectId: '', bigQueryDatasetId: '', bigQueryDataTableName: '' });
//   const [permissionsGranted, setPermissionsGranted] = useState(false);
//   const [useOwnLangfuse, setUseOwnLangfuse] = useState(false);
//   const [langfuseUrl, setLangfuseUrl] = useState('');
//   const [langfuseEmail, setLangfuseEmail] = useState('');
//   const [langfusePassword, setLangfusePassword] = useState('');
//   const [clickhouseUrl, setClickhouseUrl] = useState('');
//   const [clickhouseDbUsername, setClickhouseDbUsername] = useState('');
//   const [clickhouseDbPassword, setClickhouseDbPassword] = useState('');

//   const [serviceAccountEmail, setServiceAccountEmail] = useState<string | null>(null);
//   const [isGoBackDialogOpen, setIsGoBackDialogOpen] = useState(false);
//   const [isToastOpen, setIsToastOpen] = useState(false);
//   const [toastMessage, setToastMessage] = useState('');

//   const [getTenantAccess, { loading: setupLoading, error: setupError }] = useLazyQuery(GET_TENANT_PROVIDER_ACCESS);
//   const [createDataSource, { loading: createLoading }] = useMutation(CREATE_DATA_SOURCE_MUTATION);
//   const [validateCredentials, { loading: validateLoading }] = useMutation(VALIDATE_NEW_DATA_SOURCE_CREDENTIALS_MUTATION);

//   useEffect(() => {
//     setName('');
//     setBillingAccountId('');
//     setBigQueryProjectId('');
//     setBigQueryDatasetId('');
//     setBigQueryDataTableName('');
//     setPermissionsGranted(false);
//     setUseOwnLangfuse(false);
//     setLangfuseUrl('');
//     setLangfuseEmail('');
//     setLangfusePassword('');
//     setClickhouseUrl('');
//     setClickhouseDbUsername('');
//     setClickhouseDbPassword('');
//     setFieldErrors({ name: '', billingAccountId: '', bigQueryProjectId: '', bigQueryDatasetId: '', bigQueryDataTableName: '' });

//     if (tenantId && selectedProvider === 'google-cloud-platform') {
//         getTenantAccess({
//             variables: { tenantId, providerType: GQLProviderType.GCP },
//             onCompleted: (data) => setServiceAccountEmail(data.getTenantProviderAccess?.serviceAccountEmail || null),
//             onError: (error) => console.error("Failed to fetch service account email:", error),
//         });
//     } else {
//         setServiceAccountEmail(null);
//     }
//   }, [selectedProvider, tenantId, getTenantAccess]);

//   const gcpStepsData = useMemo(() => {
//     return [
//         { step: 'STEP 1', title: 'Enable billing export to BigQuery', description: 'Go to Billing > Billing Export > Export to BigQuery > Choose Dataset' },
//         {
//           step: 'STEP 2',
//           title: 'Assign Roles to Service Account',
//           description: serviceAccountEmail
//             ? `Give the following roles:\n * BigQuery Data Viewer\n * BigQuery Job User\nTo: The service account (${serviceAccountEmail})`
//             : (setupLoading ? 'Fetching service account details...' : 'Could not retrieve service account.')
//         },
//         { step: 'STEP 3', title: 'Provide the following information below', description: 'Enter your GCP credentials and dataset details' },
//     ];
//   }, [serviceAccountEmail, setupLoading]);

//   const externalLabelStyle = { fontFamily: 'Inter, sans-serif', fontSize: '12px', fontWeight: 400, color: theme.palette.text.primary, marginBottom: theme.spacing(0.75), display: 'block' };
//   const sxTextField = {
//     '& .MuiOutlinedInput-root': {
//       '& fieldset': { borderRadius: '8px' },
//       '&.Mui-focused fieldset': { borderColor: 'rgba(0, 0, 0, 0.23)' },
//     },
//     '& .MuiInputBase-input': {
//       fontFamily: 'Inter, sans-serif', color: theme.palette.text.primary, fontSize: '12px',
//       '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
//         WebkitBoxShadow: '0 0 0 30px white inset !important', WebkitTextFillColor: `${theme.palette.text.primary} !important`,
//         transition: 'background-color 5000s ease-in-out 0s',
//       },
//     },
//     '& .MuiInputBase-input::placeholder': {
//       fontFamily: 'Inter, sans-serif', color: 'rgba(185, 185, 185, 1)', fontSize: '12px', opacity: 1,
//     },
//   };

//   const validateField = (name: string, value: string): string => {
//     if (selectedProvider === 'llm') {
//       if (name === 'name' && !/^[a-z0-9\s]{2,50}$/.test(value)) return 'Must be 2-50 chars, lowercase letters, numbers, spaces.';
//       return '';
//     }

//     switch (name) {
//       case 'name':
//         if (!/^[a-z0-9\s]{2,50}$/.test(value)) return 'Must be 2-50 chars, lowercase letters, numbers, spaces.';
//         break;
//       case 'billingAccountId':
//         if (!/^[0-9A-F]{6}-[0-9A-F]{6}-[0-9A-F]{6}$/i.test(value)) return 'Format: XXXXXX-XXXXXX-XXXXXX (hex digits).';
//         break;
//       case 'bigQueryProjectId':
//         if (!/^[a-z][a-z0-9-]{4,28}[a-z0-9]$/.test(value)) return '6-30 chars, starts with letter, ends with letter/num, only lowercase, nums, hyphens.';
//         break;
//       case 'bigQueryDatasetId':
//         if (!/^[a-zA-Z][a-zA-Z0-9_]{0,1023}$/.test(value)) return '1-1024 chars, starts with letter, only letters, nums, underscores.';
//         break;
//       case 'bigQueryDataTableName':
//         if (!/^[a-zA-Z_][a-zA-Z0-9_-]{0,1023}$/.test(value)) {
//           return '1-1024 chars, starts with letter or underscore, can only contain letters, nums, underscores, or hyphens.';
//         }
//         if (['_table_', '_file_', '_partition_'].some(p => value.toLowerCase().startsWith(p))) {
//           return 'Cannot start with _TABLE_, _FILE_, or _PARTITION_.';
//         }
//         break;
//       default:
//         break;
//     }
//     return '';
//   };

//   const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
//     const { name, value } = event.target;
//     let errorMessage = validateField(name, value);
//     switch (name) {
//       case 'name': setName(value); break;
//       case 'billingAccountId': setBillingAccountId(value); break;
//       case 'bigQueryProjectId': setBigQueryProjectId(value); break;
//       case 'bigQueryDatasetId': setBigQueryDatasetId(value); break;
//       case 'bigQueryDataTableName': setBigQueryDataTableName(value); break;
//       case 'langfuseUrl': setLangfuseUrl(value); break;
//       case 'langfuseEmail': setLangfuseEmail(value); break;
//       case 'langfusePassword': setLangfusePassword(value); break;
//       case 'clickhouseUrl': setClickhouseUrl(value); break;
//       case 'clickhouseDbUsername': setClickhouseDbUsername(value); break;
//       case 'clickhouseDbPassword': setClickhouseDbPassword(value); break;
//       default: break;
//     }
//     setFieldErrors(prev => ({ ...prev, [name]: errorMessage }));
//   };

//   const handleGoBack = () => navigate('/data-sources');
//   const handleProviderChange = (event: React.ChangeEvent<HTMLInputElement>) => setSelectedProvider(event.target.value);

//   const canSubmit = useMemo(() => {
//     const hasNoErrors = Object.values(fieldErrors).every(error => error === '');
//     const baseChecks = !!tenantId && hasNoErrors;

//     if (selectedProvider === 'google-cloud-platform') {
//         const gcpChecks =
//             name.trim() !== '' &&
//             billingAccountId.trim() !== '' &&
//             bigQueryProjectId.trim() !== '' &&
//             bigQueryDatasetId.trim() !== '' &&
//             bigQueryDataTableName.trim() !== '' &&
//             permissionsGranted &&
//             !!serviceAccountEmail;
//         return baseChecks && gcpChecks;
//     }

//     if (selectedProvider === 'llm') {
//         if (useOwnLangfuse) {
//             return false;
//         } else {
//             return baseChecks && name.trim() !== '' && !fieldErrors.name;
//         }
//     }

//     return false;
//   }, [
//     name, tenantId, billingAccountId, bigQueryProjectId, bigQueryDatasetId, bigQueryDataTableName,
//     permissionsGranted, selectedProvider, serviceAccountEmail, fieldErrors, useOwnLangfuse
//   ]);

//   const handleToastClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
//     if (reason === 'clickaway') return;
//     setIsToastOpen(false);
//   };

//   const handleSubmit = async () => {
//     if (!canSubmit) return;

//     let input: any;

//     if (selectedProvider === 'google-cloud-platform') {
//         input = {
//             tenantId,
//             providerType: GQLProviderType.GCP,
//             name: name.trim(),
//             billingAccountId: billingAccountId.trim(),
//             bigQueryProjectId: bigQueryProjectId.trim(),
//             bigQueryDatasetId: bigQueryDatasetId.trim(),
//             bigQueryDataTableName: bigQueryDataTableName.trim(),
//         };
//     } else if (selectedProvider === 'llm') {
//         input = {
//             tenantId,
//             providerType: mapProviderToGqlType(selectedProvider),
//             name: name.trim(),
//         };
//     } else {
//         return;
//     }

//     try {
//       if (selectedProvider === 'google-cloud-platform') {
//         const validationResponse = await validateCredentials({ variables: { input } });
//         const validationResult = validationResponse.data?.validateNewDataSourceCredentials;
//         if (!validationResult?.isValid) {
//           setToastMessage("We are unable to validate the GCP credentials. Please check them and try again.");
//           setIsToastOpen(true);
//           return;
//         }
//       }

//       const createResponse = await createDataSource({ variables: { input } });
//       if (createResponse.errors && createResponse.errors.length > 0) {
//         throw new Error(createResponse.errors[0].message);
//       }
//       navigate('/data-sources', { state: { dataSourceAdded: true } });
//     } catch (e: any) {
//       console.error("Error creating data source:", e);
//       setToastMessage("We were unable to add the Data Source. Please try again later.");
//       setIsToastOpen(true);
//     }
//   };

//   const isSubmitting = validateLoading || createLoading;

//   if (!tenantId) {
//     return (
//       <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', p: 3 }}>
//         <Alert severity="error">Required tenant information is missing.</Alert>
//         <Typography sx={{mt: 2}}>This page must be accessed from the Data Sources list.</Typography>
//         <Button onClick={handleGoBack} sx={{ mt: 2 }}>Go Back</Button>
//       </Box>
//     );
//   }

//   return (
//     <Box sx={{ p: { xs: 1, sm: 2.5 } }}>
//       <Button
//         onClick={() => setIsGoBackDialogOpen(true)}
//         startIcon={<img src="/icons/back-arrow.svg" alt="Back" style={{ width: '12px', height: '12px', marginLeft:5, marginRight:3 }} />}
//         sx={{ p: 0, textTransform: 'none', color: 'text.primary', '&:hover': { backgroundColor: 'transparent' } }}
//       >
//         <Typography sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '14px' }}>
//           Add New Data Sources
//         </Typography>
//       </Button>
//       <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '12px', color: 'rgba(102, 102, 102, 1)', mt: 0.5, marginLeft:2 }}>
//         Add the Billing Account IDs and linked BigQuery dataset details associated with your organisation.
//       </Typography>

//       <Paper
//         elevation={0}
//         sx={{ height: { md: 'calc(100vh - 180px)' }, minHeight: '650px', borderRadius: '10px', backgroundColor: 'white', display: 'flex', flexDirection: 'column', overflow: 'hidden', mt: 2 }}
//       >
//         <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: theme.spacing(4), flexGrow: 1, overflow: 'auto' }}>
//           <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: theme.spacing(3), padding: theme.spacing(3) }}>
//             <Typography variant="h6" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '14px' }}>Select Provider</Typography>
//             <RadioGroup row value={selectedProvider} onChange={handleProviderChange} name="select-provider-group" sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', marginLeft:'10px' }}>
//               <FormControlLabel value="google-cloud-platform" control={<Radio sx={{ display: 'none' }} />} label={
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1, py: 1, borderRadius: '8px', border: `1px solid ${selectedProvider === 'google-cloud-platform' ? theme.palette.primary.main : theme.palette.grey[300]}`, backgroundColor: selectedProvider === 'google-cloud-platform' ? alpha(theme.palette.primary.main, 0.05) : theme.palette.grey[100], width: '255px', height: '35px', cursor: 'pointer' }}>
//                   <img src="/icons/google-cloud-icon-2048x1646-7admxejz.svg" alt="GCP" style={{ height: 20, width: 20 }} />
//                   <Typography sx={{ flexGrow: 1, fontSize: '12px' }}>Google Cloud Platform</Typography>
//                   <Box sx={{ ml: 'auto', width: 16, height: 16, borderRadius: '50%', border: `1px solid ${selectedProvider === 'google-cloud-platform' ? theme.palette.primary.main : theme.palette.text.disabled}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, '&::before': { content: '""', width: 8, height: 8, borderRadius: '50%', backgroundColor: selectedProvider === 'google-cloud-platform' ? theme.palette.primary.main : 'transparent' } }} />
//                 </Box>
//               }/>
//               <FormControlLabel value="llm" control={<Radio sx={{ display: 'none' }} />} label={
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1, py: 1, borderRadius: '8px', border: `1px solid ${selectedProvider === 'llm' ? theme.palette.primary.main : theme.palette.grey[300]}`, backgroundColor: selectedProvider === 'llm' ? alpha(theme.palette.primary.main, 0.05) : theme.palette.grey[100], width: '255px', height: '35px', cursor: 'pointer' }}>
//                   <img src="/icons/llm-observability-blue.svg" alt="LLM" style={{ height: 18, width: 18}} />
//                   <Typography sx={{ flexGrow: 1, fontSize: '12px' }}>LLM</Typography>
//                   <Box sx={{ ml: 'auto', width: 16, height: 16, borderRadius: '50%', border: `1px solid ${selectedProvider === 'llm' ? theme.palette.primary.main : theme.palette.text.disabled}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, '&::before': { content: '""', width: 8, height: 8, borderRadius: '50%', backgroundColor: selectedProvider === 'llm' ? theme.palette.primary.main : 'transparent' } }} />
//                 </Box>
//               }/>
//               <FormControlLabel value="aws" control={<Radio sx={{ display: 'none' }} />} disabled={true} label={
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1, py: 1, borderRadius: '8px', border: `1px solid ${theme.palette.grey[300]}`, backgroundColor: theme.palette.grey[100], width: '255px', height: '35px', cursor: 'not-allowed', opacity: 0.5 }}>
//                   <img src="/icons/aws.png" alt="AWS" style={{ height: 20, width: 20 }} />
//                   <Typography sx={{ flexGrow: 1, fontSize: '12px' }}>Amazon Web Services</Typography>
//                   <Box sx={{ ml: 'auto', width: 16, height: 16, borderRadius: '50%', border: `1px solid ${theme.palette.text.disabled}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} />
//                 </Box>
//               }/>
//               <FormControlLabel value="azure" control={<Radio sx={{ display: 'none' }} />} disabled={true} label={
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1, py: 1, borderRadius: '8px', border: `1px solid ${theme.palette.grey[300]}`, backgroundColor: theme.palette.grey[100], width: '255px', height: '35px', cursor: 'not-allowed', opacity: 0.5 }}>
//                   <img src="/icons/azure.svg" alt="Azure" style={{ height: 20, width: 20 }} />
//                   <Typography sx={{ flexGrow: 1, fontSize: '12px' }}>Microsoft Azure</Typography>
//                   <Box sx={{ ml: 'auto', width: 16, height: 16, borderRadius: '50%', border: `1px solid ${theme.palette.text.disabled}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} />
//                 </Box>
//               }/>
//             </RadioGroup>
//             <Divider />
            
//             <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
//                 <Typography sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '14px', color: ' #333333' }}>
//                     {selectedProvider === 'llm' ? 'LLM Details' : 'Billing Account Details'}
//                 </Typography>
//                 {selectedProvider === 'llm' && (
//                     <FormControlLabel
//                         control={<Switch checked={useOwnLangfuse} onChange={(e) => setUseOwnLangfuse(e.target.checked)} />}
//                         label="Use self hosted Langfuse"
//                         labelPlacement="start"
//                         sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '12px', fontWeight: 500, color:"#666666" } }}
//                     />
//                 )}
//             </Box>

//             <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0 }}>
//                 {selectedProvider === 'google-cloud-platform' && (
//                     <>
//                         <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1.5 }}>
//                             <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="dataSourceName" sx={externalLabelStyle}>Data Source Name *</InputLabel><TextField autoFocus required fullWidth id="dataSourceName" name="name" size="small" placeholder="Enter data source name" sx={sxTextField} value={name} onChange={handleInputChange} error={!!fieldErrors.name} helperText={fieldErrors.name} InputProps={{ endAdornment: name && !fieldErrors.name && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} /></Box>
//                             <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="billingAccountId" sx={externalLabelStyle}>Billing Account ID *</InputLabel><TextField required fullWidth id="billingAccountId" name="billingAccountId" size="small" placeholder="Enter GCP billing account ID" sx={sxTextField} value={billingAccountId} onChange={handleInputChange} error={!!fieldErrors.billingAccountId} helperText={fieldErrors.billingAccountId} InputProps={{ endAdornment: billingAccountId && !fieldErrors.billingAccountId && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} /></Box>
//                         </Box>
//                         <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1.5 }}>
//                             <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="bigQueryProjectId" sx={externalLabelStyle}>BigQuery Project ID *</InputLabel><TextField required fullWidth id="bigQueryProjectId" name="bigQueryProjectId" size="small" placeholder="Enter BigQuery project ID" sx={sxTextField} value={bigQueryProjectId} onChange={handleInputChange} error={!!fieldErrors.bigQueryProjectId} helperText={fieldErrors.bigQueryProjectId} InputProps={{ endAdornment: bigQueryProjectId && !fieldErrors.bigQueryProjectId && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} /></Box>
//                             <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="bigQueryDatasetId" sx={externalLabelStyle}>BigQuery Dataset ID *</InputLabel><TextField required fullWidth id="bigQueryDatasetId" name="bigQueryDatasetId" size="small" placeholder="Enter BigQuery dataset ID" sx={sxTextField} value={bigQueryDatasetId} onChange={handleInputChange} error={!!fieldErrors.bigQueryDatasetId} helperText={fieldErrors.bigQueryDatasetId} InputProps={{ endAdornment: bigQueryDatasetId && !fieldErrors.bigQueryDatasetId && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} /></Box>
//                         </Box>
//                         <Box sx={{ mt: 1.5, width:'49%' }}><InputLabel htmlFor="bigQueryDataTableName" sx={externalLabelStyle}>BigQuery Table Name *</InputLabel><TextField required fullWidth id="bigQueryDataTableName" name="bigQueryDataTableName" size="small" placeholder="Enter BigQuery table name" sx={sxTextField} value={bigQueryDataTableName} onChange={handleInputChange} error={!!fieldErrors.bigQueryDataTableName} helperText={fieldErrors.bigQueryDataTableName} InputProps={{ endAdornment: bigQueryDataTableName && !fieldErrors.bigQueryDataTableName && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} /></Box>
//                         <FormControlLabel control={<Checkbox checked={permissionsGranted} onChange={(e) => setPermissionsGranted(e.target.checked)}/>} label= {<Typography sx={{ fontFamily: 'Inter', fontSize: '14px', color: 'text.secondary' }}>I have granted the necessary permissions as described in the Setup Guide.</Typography>} disabled={!serviceAccountEmail || setupLoading}/>
//                     </>
//                 )}
                
//                 {selectedProvider === 'llm' && !useOwnLangfuse && (
//                     <Box sx={{ mt: -1, width: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
//                         <InputLabel htmlFor="dataSourceName" sx={externalLabelStyle}>Data Source Name *</InputLabel>
//                         <TextField autoFocus required fullWidth id="dataSourceName" name="name" size="small" placeholder="Enter your data source name" sx={sxTextField} value={name} onChange={handleInputChange} error={!!fieldErrors.name} helperText={fieldErrors.name} InputProps={{ endAdornment: name && !fieldErrors.name && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} />
//                     </Box>
//                 )}

//                 {selectedProvider === 'llm' && useOwnLangfuse && (
//                   <>
//                     <Box sx={{ mt: -2, width: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
//                         <InputLabel htmlFor="dataSourceName" sx={externalLabelStyle}>Data Source Name *</InputLabel>
//                         <TextField autoFocus required fullWidth disabled id="dataSourceName" name="name" size="small" placeholder="Enter your data source name" sx={sxTextField} value={name} onChange={handleInputChange} error={!!fieldErrors.name} helperText={fieldErrors.name} InputProps={{ endAdornment: name && !fieldErrors.name && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} />
//                     </Box>
//                     <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1.5 }}>
//                         <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="langfuseUrl" sx={externalLabelStyle}>Langfuse URL *</InputLabel><TextField required fullWidth disabled id="langfuseUrl" name="langfuseUrl" size="small" placeholder="Enter Langfuse URL" sx={sxTextField} value={langfuseUrl} onChange={handleInputChange} /></Box>
//                         <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="langfuseEmail" sx={externalLabelStyle}>Langfuse Email *</InputLabel><TextField required fullWidth disabled id="langfuseEmail" name="langfuseEmail" size="small" placeholder="Enter Langfuse Email" sx={sxTextField} value={langfuseEmail} onChange={handleInputChange} /></Box>
//                     </Box>
//                     <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1.5 }}>
//                         <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="langfusePassword" sx={externalLabelStyle}>Langfuse Password *</InputLabel><TextField required fullWidth disabled id="langfusePassword" name="langfusePassword" type="password" size="small" placeholder="Enter Langfuse Password" sx={sxTextField} value={langfusePassword} onChange={handleInputChange} /></Box>
//                         <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="clickhouseUrl" sx={externalLabelStyle}>Clickhouse URL *</InputLabel><TextField required fullWidth disabled id="clickhouseUrl" name="clickhouseUrl" size="small" placeholder="Enter Clickhouse URL" sx={sxTextField} value={clickhouseUrl} onChange={handleInputChange} /></Box>
//                     </Box>
//                     <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1.5 }}>
//                         <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="clickhouseDbUsername" sx={externalLabelStyle}>Clickhouse DB Username *</InputLabel><TextField required fullWidth disabled id="clickhouseDbUsername" name="clickhouseDbUsername" size="small" placeholder="Enter Clickhouse DB Username" sx={sxTextField} value={clickhouseDbUsername} onChange={handleInputChange} /></Box>
//                         <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="clickhouseDbPassword" sx={externalLabelStyle}>Clickhouse DB Password *</InputLabel><TextField required fullWidth disabled id="clickhouseDbPassword" name="clickhouseDbPassword" type="password" size="small" placeholder="Enter Clickhouse DB Password" sx={sxTextField} value={clickhouseDbPassword} onChange={handleInputChange} /></Box>
//                     </Box>
//                   </>
//                 )}
//             </Box>
//           </Box>
          
//           <Box sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0, display: 'flex', flexDirection: 'column', backgroundColor:'rgba(243, 248, 254, 1)'}}>
//               <Box sx={{ p: '15px', pb: 0, flexShrink: 0 }}>
//                 <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',backgroundColor:'rgba(228, 240, 253, 1)', p: '12px', borderRadius: '4px' }}>
//                     <Box sx={{ width :'250px', mr: 1 }}>
//                         <Typography variant="h5" sx={{ fontSize:'12px',fontWeight: 600,mb:0.5 }}>Setup Guide</Typography>
//                         <Typography variant="body2" sx={{ fontSize:'11px',fontWeight: 400 }}>Step-by-step guide to set up data access.</Typography>
//                     </Box>
//                     <img src="/icons/step-guide.svg" alt="Setup Guide" style={{ height: '70px', width: '70px', flexShrink: 0 }} />
//                 </Box>
//               </Box>

//               <Box sx={{ flexGrow: 1, overflowY: 'auto', p: '15px', pt: 2 }}>
//                 {selectedProvider === 'google-cloud-platform' && (
//                   <>
//                       {setupLoading && <CircularProgress size={20} sx={{alignSelf: 'center'}} />}
//                       {gcpStepsData.map((item) => (
//                           <Box sx={{ mb: 2 }} textAlign={'left'} key={item.step}>
//                               <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main', textTransform: 'uppercase' }}>{item.step}</Typography>
//                               <Typography sx={{ fontSize: '12px', fontWeight: 600, whiteSpace: 'pre-line' }}>{item.title}</Typography>
//                               <Typography variant="body2" sx={{ fontSize: '11px', color: 'text.secondary', whiteSpace: 'pre-line', wordBreak: 'break-word' }}>{item.description}</Typography>
//                           </Box>
//                       ))}
//                       {setupError && <Alert severity="error">Failed to get Service Account Email: {setupError.message}</Alert>}
//                   </>
//                 )}
//                 {selectedProvider === 'llm' && <LlmSetupGuide />}
//               </Box>
//           </Box>
//         </Box>
//         <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, p: 2, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
//           <Button variant="text" href="https://cloud.google.com/billing/docs/how-to/find-billing-account-id" target="_blank" rel="noopener noreferrer" sx={{ color:'rgba(0, 100, 255, 1)', mr: 'auto', textTransform: 'none', textDecoration: 'none' }} startIcon={<img src="/linkurl.svg" alt="Help icon" width="16" height="16"/>}>
//             Where do I find my Billing Account ID?
//           </Button>
//           <Button sx={{ backgroundColor:' #F2F7FF',textTransform: 'none', fontFamily:'Inter', borderRadius:'8px',borderColor: '#0064FF', color:'#0064FF'}} onClick={() => setIsGoBackDialogOpen(true)}>Cancel</Button>
//           <Button
//             variant="contained"
//             sx={{ textTransform: 'none',fontFamily:'Inter', borderRadius:'8px', backgroundColor:' #0064FF',color:'white' }}
//             onClick={handleSubmit}
//             disabled={!canSubmit || isSubmitting}
//             startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
//           >
//             {isSubmitting ? (validateLoading ? 'Validating...' : 'Adding...') : 'Add Data Source'}
//           </Button>
//         </Box>
//       </Paper>
//       <Snackbar open={isToastOpen} autoHideDuration={6000} onClose={handleToastClose} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
//         <Alert icon={<CustomErrorIcon />} onClose={handleToastClose} sx={{ width: '100%', borderRadius: '50px', backgroundColor: 'white', boxShadow: theme.shadows[3], color: 'text.secondary', alignItems: 'center', p: theme.spacing(0.5, 2), '& .MuiAlert-message': { flex: 1, py: '12px' } }}>
//           {toastMessage}
//         </Alert>
//       </Snackbar>
//       <GoBackDialog open={isGoBackDialogOpen} onClose={() => setIsGoBackDialogOpen(false)} onConfirm={() => navigate('/data-sources')} />
//     </Box>
//   );
// };

// export default AddDataSourcesPage;





// import React, { useState, useMemo, useEffect } from 'react';
// import {
//   Box,
//   Typography,
//   Button,
//   TextField,
//   RadioGroup,
//   FormControlLabel,
//   Radio,
//   Paper,
//   Divider,
//   InputLabel,
//   CircularProgress,
//   Checkbox,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   Snackbar,
//   Alert,
//   Link,
//   InputAdornment,
//   Switch,
// } from '@mui/material';
// import { useTheme } from '@mui/material/styles';
// import { alpha } from '@mui/material/styles';
// import { GitHub as GitHubIcon, Close as CloseIcon } from '@mui/icons-material';
// import { useNavigate, useOutletContext } from 'react-router-dom';
// import { gql, useLazyQuery, useMutation } from '@apollo/client';
// import type { Tenant } from '../../App';
// import { Check as CheckIcon } from '@mui/icons-material';


// // --- CHANGE 1: Updated GQLProviderType enum to match backend expectation ---
// export enum GQLProviderType {
//   GCP = "GCP",
//   LANGFUSE = "LANGFUSE",
//   AWS = "AWS",
//   AZURE = "Azure",
// }

// // --- Custom icon for the error toast ---
// const CustomErrorIcon = () => (
//     <Box
//       component="span"
//       sx={{
//         width: 24,
//         height: 24,
//         borderRadius: '50%',
//         display: 'flex',
//         alignItems: 'center',
//         justifyContent: 'center',
//         backgroundColor: '#D92D20',
//       }}
//     >
//       <CloseIcon sx={{ color: 'white', fontSize: '16px' }} />
//     </Box>
// );

// // --- GoBackDialog to confirm leaving the page ---
// const GoBackDialog = ({ open, onClose, onConfirm }: { open: boolean, onClose: () => void, onConfirm: () => void }) => {
//   if (!open) return null;

//   const backgroundTextureUrl = '/textures/dialog-background-texture.svg';
//   const goBackIconUrl = '/icons/go-back-dialog-icon.svg';

//   return (
//     <Dialog
//       open={open}
//       onClose={onClose}
//       PaperProps={{
//         sx: {
//           borderRadius: '24px',
//           width: '100%',
//           maxWidth: '520px',
//           p: 0.5,
//           position: 'relative',
//           overflow: 'hidden',
//           backgroundImage: `url(${backgroundTextureUrl})`,
//           backgroundRepeat: 'no-repeat',
//           backgroundPosition: 'top right',
//           backgroundColor: 'white',
//         },
//       }}
//     >
//       <DialogContent sx={{ p: 3, pt: 4, zIndex: 1 }}>
//         <Box
//           sx={{
//             mb: 3,
//             width: 50,
//             height: 50,
//             borderRadius: '12px',
//             display: 'flex',
//             alignItems: 'center',
//             justifyContent: 'center',
//             backgroundColor:' #E5EFFF'
//           }}
//         >
//           <img src={goBackIconUrl} alt="Go back icon" style={{ width: 19, height: 20 }} />
//         </Box>
//         <Typography sx={{ fontWeight: 600, mb: 1, fontSize: '16px', color: '#333333' }}>
//           Go Back
//         </Typography>
//         <Typography variant="body1" sx={{ color: ' #333333', fontSize: '14px', lineHeight: 1.6, fontWeight:400 }}>
//           Are you sure you want to leave this page? Leaving this page
//           will discard all the progress made in the below form.
//         </Typography>
//       </DialogContent>
//       <DialogActions sx={{ p: 3, justifyContent: 'flex-end', gap: 1.5, zIndex: 1 }}>
//         <Button
//           onClick={onClose}
//           sx={{
//             textTransform: 'none',
//             borderRadius: '8px',
//             backgroundColor: '#EFF6FF',
//             color: '#0064FF',
//             fontWeight: 500,
//             fontSize: '14px',
//             padding: '10px 20px',
//           }}
//         >
//           Cancel
//         </Button>
//         <Button
//           onClick={onConfirm}
//           size="large"
//           sx={{
//             textTransform: 'none',
//             borderRadius: '8px',
//             backgroundColor: '#0064FF',
//             fontWeight: 500,
//             fontSize: '14px',
//             padding: '10px 20px',
//             boxShadow: 'none',
//             color:'white'
//           }}
//         >
//           Yes, Go Back
//         </Button>
//       </DialogActions>
//     </Dialog>
//   );
// };

// // --- Component for LLM SDK Setup Guide ---
// const LlmSetupGuide = () => {
//     const [activeManager, setActiveManager] = useState('npm');

//     const commands = {
//         npm: 'npm install langfuse\n\n# Node.js < 18\nnpm install langfuse-node',
//         pnpm: 'pnpm add langfuse\n\n# Node.js < 18\npnpm add langfuse-node',
//         yarn: 'yarn add langfuse\n\n# Node.js < 18\nyarn add langfuse-node',
//         bun: 'bun add langfuse\n\n# Node.js < 18\nbun add langfuse-node',
//     };

//     const Badge = ({ label, value, color, href }: { label: string, value: string, color: string, href?: string }) => (
//         <Button
//             component={href ? 'a' : 'button'}
//             href={href}
//             target="_blank"
//             rel="noopener noreferrer"
//             sx={{
//                 p: 0,
//                 m: 0,
//                 height: '20px',
//                 textTransform: 'none',
//                 borderRadius: '4px',
//                 overflow: 'hidden',
//                 '&:hover': {
//                     ...(href && {
//                         opacity: 0.8
//                     })
//                 }
//             }}>
//             <Box sx={{ bgcolor: '#555', color: 'white', px: 1, height: '100%', display: 'flex', alignItems: 'center' }}>
//                 {label === 'repo' && <GitHubIcon sx={{ fontSize: '14px', mr: 0.5 }} />}
//                 <Typography sx={{ fontSize: '10px', fontWeight: 500 }}>{label}</Typography>
//             </Box>
//             <Box sx={{ bgcolor: color, color: 'white', px: 1, height: '100%', display: 'flex', alignItems: 'center' }}>
//                 <Typography sx={{ fontSize: '10px', fontWeight: 500 }}>{value}</Typography>
//             </Box>
//         </Button>
//     );

//     const TabButton = ({ manager }: { manager: 'npm' | 'pnpm' | 'yarn' | 'bun' }) => (
//         <Button
//             onClick={() => setActiveManager(manager)}
//             sx={{
//                 p: '2px 8px',
//                 minWidth: 'auto',
//                 color: activeManager === manager ? 'primary.main' : 'text.secondary',
//                 fontWeight: 500,
//                 fontSize: '13px',
//                 borderBottom: 2,
//                 borderColor: activeManager === manager ? 'primary.main' : 'transparent',
//                 borderRadius: 0,
//                 textTransform: 'none'
//             }}
//         >
//             {manager}
//         </Button>
//     );

//     return (
//         <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
//           <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>LLM SDK Setup</Typography>
//           <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
//               The Langfuse SDKs are the recommended way to integrate with Langfuse.{' '}
//               <Link
//                 href="https://langfuse.com/integrations"
//                 target="_blank"
//                 rel="noopener noreferrer"
//                 sx={{ fontWeight: 500 }}
//               >
//                 [See a full list of Integration]
//               </Link>
//           </Typography>
//           <Divider sx={{ my: 1 }} />
//           <Typography sx={{ fontSize: '12px', color: '#333333', fontWeight:600 }}>
//               Install the Toolkit
//           </Typography>
//           <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
//               Pick your language and run one of these commands in your project's terminal.
//           </Typography>

//           <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>JS/TS</Typography>
//           <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
//               <Badge label="repo" value="langfuse-js" color="#007ec6" href="https://github.com/langfuse/langfuse-js" />
//               <Badge label="npm langfuse" value="v3.38.4" color="#007ec6" />
//               <Badge label="npm langfuse-node" value="v3.38.4" color="#007ec6" />
//           </Box>
//           <Box sx={{ display: 'flex', borderBottom: 1, borderColor: 'divider' }}>
//               <TabButton manager="npm" />
//               <TabButton manager="pnpm" />
//               <TabButton manager="yarn" />
//               <TabButton manager="bun" />
//           </Box>
//           <Box sx={{ bgcolor: '#f7f7f7', p: 1.5, borderRadius: '4px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', fontSize: '12px', border: '1px solid #e0e0e0' }}>
//               {commands[activeManager as keyof typeof commands]}
//           </Box>

//           <Divider sx={{ my: 2 }} />

//           <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>Python</Typography>
//           <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
//               <Badge label="repo" value="langfuse-python" color="#007ec6" href="https://github.com/langfuse/langfuse-python" />
//               <Badge label="pypi langfuse" value="v3.2.3" color="#007ec6" />
//           </Box>
//           <Box sx={{ bgcolor: '#f7f7f7', p: 1.5, borderRadius: '4px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', fontSize: '12px', border: '1px solid #e0e0e0', mt: 1 }}>
//               pip install langfuse
//           </Box>
//         </Box>
//     );
// };
// // --- END: Component ---

// // --- GraphQL Definitions ---
// const GET_TENANT_PROVIDER_ACCESS = gql`
//   query GetTenantProviderAccess($tenantId: ID!, $providerType: ProviderType!) {
//     getTenantProviderAccess(tenantId: $tenantId, providerType: $providerType) {
//       serviceAccountEmail
//     }
//   }
// `;

// const CREATE_DATA_SOURCE_MUTATION = gql`
//   mutation CreateDataSource($input: CreateDataSourceInput!) {
//     createDataSource(input: $input) {
//       __typename
//       ... on DataSource {
//         id
//         name
//       }
//       ... on LlmDataSource {
//         id
//         name
//       }
//     }
//   }
// `;

// const VALIDATE_NEW_DATA_SOURCE_CREDENTIALS_MUTATION = gql`
//   mutation ValidateNewDataSourceCredentials($input: ValidateNewDataSourceCredentialsInput!) {
//     validateNewDataSourceCredentials(input: $input) {
//       isValid
//       validationErrors
//     }
//   }
// `;

// // --- CHANGE 2: Updated mapping function to send the correct backend value ---
// const mapProviderToGqlType = (frontendProviderValue: string): GQLProviderType => {
//   switch (frontendProviderValue) {
//     case 'google-cloud-platform': return GQLProviderType.GCP;
//     case 'llm': return GQLProviderType.LANGFUSE;
//     case 'aws': return GQLProviderType.AWS;
//     case 'azure': return GQLProviderType.AZURE;
//     default: throw new Error(`Unsupported provider value: ${frontendProviderValue}`);
//   }
// };

// interface AddDataSourceOutletContext {
//   currentTenant: Tenant | null;
// }

// const AddDataSourcesPage: React.FC = () => {
//   const theme = useTheme();
//   const navigate = useNavigate();
//   const { currentTenant } = useOutletContext<AddDataSourceOutletContext>();
//   const tenantId = currentTenant?.id;

//   // Form State
//   const [name, setName] = useState('');
//   const [billingAccountId, setBillingAccountId] = useState('');
//   const [bigQueryProjectId, setBigQueryProjectId] = useState('');
//   const [bigQueryDatasetId, setBigQueryDatasetId] = useState('');
//   const [bigQueryDataTableName, setBigQueryDataTableName] = useState('');
//   const [selectedProvider, setSelectedProvider] = useState('google-cloud-platform');
//   const [fieldErrors, setFieldErrors] = useState({ name: '', billingAccountId: '', bigQueryProjectId: '', bigQueryDatasetId: '', bigQueryDataTableName: '' });
//   const [permissionsGranted, setPermissionsGranted] = useState(false);
//   // --- NEW STATE FOR LANGFUSE ---
//   const [useOwnLangfuse, setUseOwnLangfuse] = useState(false);
//   const [langfuseUrl, setLangfuseUrl] = useState('');
//   const [langfuseEmail, setLangfuseEmail] = useState('');
//   const [langfusePassword, setLangfusePassword] = useState('');
//   const [clickhouseUrl, setClickhouseUrl] = useState('');
//   const [clickhouseDbUsername, setClickhouseDbUsername] = useState('');
//   const [clickhouseDbPassword, setClickhouseDbPassword] = useState('');
//   // --- END NEW STATE ---

//   // Other component state
//   const [serviceAccountEmail, setServiceAccountEmail] = useState<string | null>(null);
//   const [isGoBackDialogOpen, setIsGoBackDialogOpen] = useState(false);
//   const [isToastOpen, setIsToastOpen] = useState(false);
//   const [toastMessage, setToastMessage] = useState('');

//   // Hooks for GraphQL operations
//   const [getTenantAccess, { loading: setupLoading, error: setupError }] = useLazyQuery(GET_TENANT_PROVIDER_ACCESS);
//   const [createDataSource, { loading: createLoading }] = useMutation(CREATE_DATA_SOURCE_MUTATION);
//   const [validateCredentials, { loading: validateLoading }] = useMutation(VALIDATE_NEW_DATA_SOURCE_CREDENTIALS_MUTATION);

//   useEffect(() => {
//     // Reset fields when provider changes
//     setName('');
//     setBillingAccountId('');
//     setBigQueryProjectId('');
//     setBigQueryDatasetId('');
//     setBigQueryDataTableName('');
//     setPermissionsGranted(false);
//     setUseOwnLangfuse(false);
//     setLangfuseUrl('');
//     setLangfuseEmail('');
//     setLangfusePassword('');
//     setClickhouseUrl('');
//     setClickhouseDbUsername('');
//     setClickhouseDbPassword('');
//     setFieldErrors({ name: '', billingAccountId: '', bigQueryProjectId: '', bigQueryDatasetId: '', bigQueryDataTableName: '' });

//     if (tenantId && selectedProvider === 'google-cloud-platform') {
//         getTenantAccess({
//             variables: { tenantId, providerType: GQLProviderType.GCP },
//             onCompleted: (data) => setServiceAccountEmail(data.getTenantProviderAccess?.serviceAccountEmail || null),
//             onError: (error) => console.error("Failed to fetch service account email:", error),
//         });
//     } else {
//         setServiceAccountEmail(null);
//     }
//   }, [selectedProvider, tenantId, getTenantAccess]);

//   const gcpStepsData = useMemo(() => {
//     return [
//         { step: 'STEP 1', title: 'Enable billing export to BigQuery', description: 'Go to Billing > Billing Export > Export to BigQuery > Choose Dataset' },
//         {
//           step: 'STEP 2',
//           title: 'Assign Roles to Service Account',
//           description: serviceAccountEmail
//             ? `Give the following roles:\n * BigQuery Data Viewer\n * BigQuery Job User\nTo: The service account (${serviceAccountEmail})`
//             : (setupLoading ? 'Fetching service account details...' : 'Could not retrieve service account.')
//         },
//         { step: 'STEP 3', title: 'Provide the following information below', description: 'Enter your GCP credentials and dataset details' },
//     ];
//   }, [serviceAccountEmail, setupLoading]);

//   const externalLabelStyle = { fontFamily: 'Inter, sans-serif', fontSize: '12px', fontWeight: 400, color: theme.palette.text.primary, marginBottom: theme.spacing(0.75), display: 'block' };
//   const sxTextField = {
//     '& .MuiOutlinedInput-root': {
//       '& fieldset': { borderRadius: '8px' },
//       '&.Mui-focused fieldset': { borderColor: 'rgba(0, 0, 0, 0.23)' },
//     },
//     '& .MuiInputBase-input': {
//       fontFamily: 'Inter, sans-serif', color: theme.palette.text.primary, fontSize: '12px',
//       '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
//         WebkitBoxShadow: '0 0 0 30px white inset !important', WebkitTextFillColor: `${theme.palette.text.primary} !important`,
//         transition: 'background-color 5000s ease-in-out 0s',
//       },
//     },
//     '& .MuiInputBase-input::placeholder': {
//       fontFamily: 'Inter, sans-serif', color: 'rgba(185, 185, 185, 1)', fontSize: '12px', opacity: 1,
//     },
//   };

//   const validateField = (name: string, value: string): string => {
//     if (selectedProvider === 'llm') {
//       if (name === 'name' && !/^[a-z0-9\s]{2,50}$/.test(value)) return 'Must be 2-50 chars, lowercase letters, numbers, spaces.';
//       return '';
//     }

//     switch (name) {
//       case 'name':
//         if (!/^[a-z0-9\s]{2,50}$/.test(value)) return 'Must be 2-50 chars, lowercase letters, numbers, spaces.';
//         break;
//       case 'billingAccountId':
//         if (!/^[0-9A-F]{6}-[0-9A-F]{6}-[0-9A-F]{6}$/i.test(value)) return 'Format: XXXXXX-XXXXXX-XXXXXX (hex digits).';
//         break;
//       case 'bigQueryProjectId':
//         if (!/^[a-z][a-z0-9-]{4,28}[a-z0-9]$/.test(value)) return '6-30 chars, starts with letter, ends with letter/num, only lowercase, nums, hyphens.';
//         break;
//       case 'bigQueryDatasetId':
//         if (!/^[a-zA-Z][a-zA-Z0-9_]{0,1023}$/.test(value)) return '1-1024 chars, starts with letter, only letters, nums, underscores.';
//         break;
//       case 'bigQueryDataTableName':
//         if (!/^[a-zA-Z_][a-zA-Z0-9_-]{0,1023}$/.test(value)) {
//           return '1-1024 chars, starts with letter or underscore, can only contain letters, nums, underscores, or hyphens.';
//         }
//         if (['_table_', '_file_', '_partition_'].some(p => value.toLowerCase().startsWith(p))) {
//           return 'Cannot start with _TABLE_, _FILE_, or _PARTITION_.';
//         }
//         break;
//       default:
//         break;
//     }
//     return '';
//   };

//   const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
//     const { name, value } = event.target;
//     let errorMessage = validateField(name, value);
//     switch (name) {
//       case 'name': setName(value); break;
//       case 'billingAccountId': setBillingAccountId(value); break;
//       case 'bigQueryProjectId': setBigQueryProjectId(value); break;
//       case 'bigQueryDatasetId': setBigQueryDatasetId(value); break;
//       case 'bigQueryDataTableName': setBigQueryDataTableName(value); break;
//       case 'langfuseUrl': setLangfuseUrl(value); break;
//       case 'langfuseEmail': setLangfuseEmail(value); break;
//       case 'langfusePassword': setLangfusePassword(value); break;
//       case 'clickhouseUrl': setClickhouseUrl(value); break;
//       case 'clickhouseDbUsername': setClickhouseDbUsername(value); break;
//       case 'clickhouseDbPassword': setClickhouseDbPassword(value); break;
//       default: break;
//     }
//     setFieldErrors(prev => ({ ...prev, [name]: errorMessage }));
//   };

//   const handleGoBack = () => navigate('/data-sources');
//   const handleProviderChange = (event: React.ChangeEvent<HTMLInputElement>) => setSelectedProvider(event.target.value);

//   const canSubmit = useMemo(() => {
//     const hasNoErrors = Object.values(fieldErrors).every(error => error === '');
//     const baseChecks = !!tenantId && hasNoErrors;

//     if (selectedProvider === 'google-cloud-platform') {
//         const gcpChecks =
//             name.trim() !== '' &&
//             billingAccountId.trim() !== '' &&
//             bigQueryProjectId.trim() !== '' &&
//             bigQueryDatasetId.trim() !== '' &&
//             bigQueryDataTableName.trim() !== '' &&
//             permissionsGranted &&
//             !!serviceAccountEmail;
//         return baseChecks && gcpChecks;
//     }

//     if (selectedProvider === 'llm') {
//         if (useOwnLangfuse) {
//             // It's a preview, so disable submission.
//             return false;
//         } else {
//             // For the managed version, only the name is required.
//             return baseChecks && name.trim() !== '' && !fieldErrors.name;
//         }
//     }

//     return false;
//   }, [
//     name, tenantId, billingAccountId, bigQueryProjectId, bigQueryDatasetId, bigQueryDataTableName,
//     permissionsGranted, selectedProvider, serviceAccountEmail, fieldErrors, useOwnLangfuse
//   ]);

//   const handleToastClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
//     if (reason === 'clickaway') return;
//     setIsToastOpen(false);
//   };

//   const handleSubmit = async () => {
//     if (!canSubmit) return;

//     let input: any;

//     if (selectedProvider === 'google-cloud-platform') {
//         input = {
//             tenantId,
//             providerType: GQLProviderType.GCP,
//             name: name.trim(),
//             billingAccountId: billingAccountId.trim(),
//             bigQueryProjectId: bigQueryProjectId.trim(),
//             bigQueryDatasetId: bigQueryDatasetId.trim(),
//             bigQueryDataTableName: bigQueryDataTableName.trim(),
//         };
//     } else if (selectedProvider === 'llm') {
//         // The self-hosted path is blocked by canSubmit, so we only handle the managed case.
//         input = {
//             tenantId,
//             providerType: mapProviderToGqlType(selectedProvider), // Correctly maps 'llm' to 'langfuse'
//             name: name.trim(),
//         };
//     } else {
//         // Should not happen
//         return;
//     }

//     try {
//       if (selectedProvider === 'google-cloud-platform') {
//         const validationResponse = await validateCredentials({ variables: { input } });
//         const validationResult = validationResponse.data?.validateNewDataSourceCredentials;
//         if (!validationResult?.isValid) {
//           setToastMessage("We are unable to validate the GCP credentials. Please check them and try again.");
//           setIsToastOpen(true);
//           return;
//         }
//       }

//       const createResponse = await createDataSource({ variables: { input } });
//       if (createResponse.errors && createResponse.errors.length > 0) {
//         throw new Error(createResponse.errors[0].message);
//       }
//       navigate('/data-sources', { state: { dataSourceAdded: true } });
//     } catch (e: any) {
//       console.error("Error creating data source:", e);
//       setToastMessage("We were unable to add the Data Source. Please try again later.");
//       setIsToastOpen(true);
//     }
//   };

//   const isSubmitting = validateLoading || createLoading;

//   if (!tenantId) {
//     return (
//       <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', p: 3 }}>
//         <Alert severity="error">Required tenant information is missing.</Alert>
//         <Typography sx={{mt: 2}}>This page must be accessed from the Data Sources list.</Typography>
//         <Button onClick={handleGoBack} sx={{ mt: 2 }}>Go Back</Button>
//       </Box>
//     );
//   }

//   return (
//     <Box sx={{ p: { xs: 1, sm: 2.5 } }}>
//       <Button
//         onClick={() => setIsGoBackDialogOpen(true)}
//         startIcon={<img src="/icons/back-arrow.svg" alt="Back" style={{ width: '12px', height: '12px', marginLeft:5, marginRight:3 }} />}
//         sx={{ p: 0, textTransform: 'none', color: 'text.primary', '&:hover': { backgroundColor: 'transparent' } }}
//       >
//         <Typography sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '14px' }}>
//           Add New Data Sources
//         </Typography>
//       </Button>
//       <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '12px', color: 'rgba(102, 102, 102, 1)', mt: 0.5, marginLeft:2 }}>
//         Add the Billing Account IDs and linked BigQuery dataset details associated with your organisation.
//       </Typography>

//       <Paper
//         elevation={0}
//         sx={{ height: { md: 'calc(100vh - 180px)' }, minHeight: '650px', borderRadius: '10px', backgroundColor: 'white', display: 'flex', flexDirection: 'column', overflow: 'hidden', mt: 2 }}
//       >
//         <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: theme.spacing(4), flexGrow: 1, overflow: 'auto' }}>
//           <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: theme.spacing(3), padding: theme.spacing(3) }}>
//             <Typography variant="h6" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '14px' }}>Select Provider</Typography>
//             <RadioGroup row value={selectedProvider} onChange={handleProviderChange} name="select-provider-group" sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', marginLeft:'10px' }}>
//               <FormControlLabel value="google-cloud-platform" control={<Radio sx={{ display: 'none' }} />} label={
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1, py: 1, borderRadius: '8px', border: `1px solid ${selectedProvider === 'google-cloud-platform' ? theme.palette.primary.main : theme.palette.grey[300]}`, backgroundColor: selectedProvider === 'google-cloud-platform' ? alpha(theme.palette.primary.main, 0.05) : theme.palette.grey[100], width: '255px', height: '35px', cursor: 'pointer' }}>
//                   <img src="/icons/google-cloud-icon-2048x1646-7admxejz.svg" alt="GCP" style={{ height: 20, width: 20 }} />
//                   <Typography sx={{ flexGrow: 1, fontSize: '12px' }}>Google Cloud Platform</Typography>
//                   <Box sx={{ ml: 'auto', width: 16, height: 16, borderRadius: '50%', border: `1px solid ${selectedProvider === 'google-cloud-platform' ? theme.palette.primary.main : theme.palette.text.disabled}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, '&::before': { content: '""', width: 8, height: 8, borderRadius: '50%', backgroundColor: selectedProvider === 'google-cloud-platform' ? theme.palette.primary.main : 'transparent' } }} />
//                 </Box>
//               }/>
//               <FormControlLabel value="llm" control={<Radio sx={{ display: 'none' }} />} label={
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1, py: 1, borderRadius: '8px', border: `1px solid ${selectedProvider === 'llm' ? theme.palette.primary.main : theme.palette.grey[300]}`, backgroundColor: selectedProvider === 'llm' ? alpha(theme.palette.primary.main, 0.05) : theme.palette.grey[100], width: '255px', height: '35px', cursor: 'pointer' }}>
//                   <img src="/icons/llm-observability-blue.svg" alt="LLM" style={{ height: 18, width: 18}} />
//                   <Typography sx={{ flexGrow: 1, fontSize: '12px' }}>LLM</Typography>
//                   <Box sx={{ ml: 'auto', width: 16, height: 16, borderRadius: '50%', border: `1px solid ${selectedProvider === 'llm' ? theme.palette.primary.main : theme.palette.text.disabled}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, '&::before': { content: '""', width: 8, height: 8, borderRadius: '50%', backgroundColor: selectedProvider === 'llm' ? theme.palette.primary.main : 'transparent' } }} />
//                 </Box>
//               }/>
//               <FormControlLabel value="aws" control={<Radio sx={{ display: 'none' }} />} disabled={true} label={
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1, py: 1, borderRadius: '8px', border: `1px solid ${theme.palette.grey[300]}`, backgroundColor: theme.palette.grey[100], width: '255px', height: '35px', cursor: 'not-allowed', opacity: 0.5 }}>
//                   <img src="/icons/aws.png" alt="AWS" style={{ height: 20, width: 20 }} />
//                   <Typography sx={{ flexGrow: 1, fontSize: '12px' }}>Amazon Web Services</Typography>
//                   <Box sx={{ ml: 'auto', width: 16, height: 16, borderRadius: '50%', border: `1px solid ${theme.palette.text.disabled}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} />
//                 </Box>
//               }/>
//               <FormControlLabel value="azure" control={<Radio sx={{ display: 'none' }} />} disabled={true} label={
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1, py: 1, borderRadius: '8px', border: `1px solid ${theme.palette.grey[300]}`, backgroundColor: theme.palette.grey[100], width: '255px', height: '35px', cursor: 'not-allowed', opacity: 0.5 }}>
//                   <img src="/icons/azure.svg" alt="Azure" style={{ height: 20, width: 20 }} />
//                   <Typography sx={{ flexGrow: 1, fontSize: '12px' }}>Microsoft Azure</Typography>
//                   <Box sx={{ ml: 'auto', width: 16, height: 16, borderRadius: '50%', border: `1px solid ${theme.palette.text.disabled}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} />
//                 </Box>
//               }/>
//             </RadioGroup>
//             <Divider />
            
//             <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
//                 <Typography sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '14px', color: ' #333333' }}>
//                     {selectedProvider === 'llm' ? 'LLM Details' : 'Billing Account Details'}
//                 </Typography>
//                 {selectedProvider === 'llm' && (
//                     <FormControlLabel
//                         control={<Switch checked={useOwnLangfuse} onChange={(e) => setUseOwnLangfuse(e.target.checked)} />}
//                         label="Use self hosted Langfuse"
//                         labelPlacement="start"
//                         sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '12px', fontWeight: 500, color:"#666666" } }}
//                     />
//                 )}
//             </Box>

//             <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0 }}>
//                 {selectedProvider === 'google-cloud-platform' && (
//                     <>
//                         <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1.5 }}>
//                             <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="dataSourceName" sx={externalLabelStyle}>Data Source Name *</InputLabel><TextField autoFocus required fullWidth id="dataSourceName" name="name" size="small" placeholder="Enter data source name" sx={sxTextField} value={name} onChange={handleInputChange} error={!!fieldErrors.name} helperText={fieldErrors.name} InputProps={{ endAdornment: name && !fieldErrors.name && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} /></Box>
//                             <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="billingAccountId" sx={externalLabelStyle}>Billing Account ID *</InputLabel><TextField required fullWidth id="billingAccountId" name="billingAccountId" size="small" placeholder="Enter GCP billing account ID" sx={sxTextField} value={billingAccountId} onChange={handleInputChange} error={!!fieldErrors.billingAccountId} helperText={fieldErrors.billingAccountId} InputProps={{ endAdornment: billingAccountId && !fieldErrors.billingAccountId && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} /></Box>
//                         </Box>
//                         <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1.5 }}>
//                             <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="bigQueryProjectId" sx={externalLabelStyle}>BigQuery Project ID *</InputLabel><TextField required fullWidth id="bigQueryProjectId" name="bigQueryProjectId" size="small" placeholder="Enter BigQuery project ID" sx={sxTextField} value={bigQueryProjectId} onChange={handleInputChange} error={!!fieldErrors.bigQueryProjectId} helperText={fieldErrors.bigQueryProjectId} InputProps={{ endAdornment: bigQueryProjectId && !fieldErrors.bigQueryProjectId && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} /></Box>
//                             <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="bigQueryDatasetId" sx={externalLabelStyle}>BigQuery Dataset ID *</InputLabel><TextField required fullWidth id="bigQueryDatasetId" name="bigQueryDatasetId" size="small" placeholder="Enter BigQuery dataset ID" sx={sxTextField} value={bigQueryDatasetId} onChange={handleInputChange} error={!!fieldErrors.bigQueryDatasetId} helperText={fieldErrors.bigQueryDatasetId} InputProps={{ endAdornment: bigQueryDatasetId && !fieldErrors.bigQueryDatasetId && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} /></Box>
//                         </Box>
//                         <Box sx={{ mt: 1.5, width:'49%' }}><InputLabel htmlFor="bigQueryDataTableName" sx={externalLabelStyle}>BigQuery Table Name *</InputLabel><TextField required fullWidth id="bigQueryDataTableName" name="bigQueryDataTableName" size="small" placeholder="Enter BigQuery table name" sx={sxTextField} value={bigQueryDataTableName} onChange={handleInputChange} error={!!fieldErrors.bigQueryDataTableName} helperText={fieldErrors.bigQueryDataTableName} InputProps={{ endAdornment: bigQueryDataTableName && !fieldErrors.bigQueryDataTableName && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} /></Box>
//                         <FormControlLabel control={<Checkbox checked={permissionsGranted} onChange={(e) => setPermissionsGranted(e.target.checked)}/>} label= {<Typography sx={{ fontFamily: 'Inter', fontSize: '14px', color: 'text.secondary' }}>I have granted the necessary permissions as described in the Setup Guide.</Typography>} disabled={!serviceAccountEmail || setupLoading}/>
//                     </>
//                 )}
                
//                 {selectedProvider === 'llm' && !useOwnLangfuse && (
//                     <Box sx={{ mt: -1, width: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
//                         <InputLabel htmlFor="dataSourceName" sx={externalLabelStyle}>Data Source Name *</InputLabel>
//                         <TextField autoFocus required fullWidth id="dataSourceName" name="name" size="small" placeholder="Enter your data source name" sx={sxTextField} value={name} onChange={handleInputChange} error={!!fieldErrors.name} helperText={fieldErrors.name} InputProps={{ endAdornment: name && !fieldErrors.name && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} />
//                     </Box>
//                 )}

//                 {selectedProvider === 'llm' && useOwnLangfuse && (
//                   <>
//                     <Box sx={{ mt: -2, width: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
//                         <InputLabel htmlFor="dataSourceName" sx={externalLabelStyle}>Data Source Name *</InputLabel>
//                         <TextField autoFocus required fullWidth disabled id="dataSourceName" name="name" size="small" placeholder="Enter your data source name" sx={sxTextField} value={name} onChange={handleInputChange} error={!!fieldErrors.name} helperText={fieldErrors.name} InputProps={{ endAdornment: name && !fieldErrors.name && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} />
//                     </Box>
//                     <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1.5 }}>
//                         <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="langfuseUrl" sx={externalLabelStyle}>Langfuse URL *</InputLabel><TextField required fullWidth disabled id="langfuseUrl" name="langfuseUrl" size="small" placeholder="Enter Langfuse URL" sx={sxTextField} value={langfuseUrl} onChange={handleInputChange} /></Box>
//                         <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="langfuseEmail" sx={externalLabelStyle}>Langfuse Email *</InputLabel><TextField required fullWidth disabled id="langfuseEmail" name="langfuseEmail" size="small" placeholder="Enter Langfuse Email" sx={sxTextField} value={langfuseEmail} onChange={handleInputChange} /></Box>
//                     </Box>
//                     <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1.5 }}>
//                         <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="langfusePassword" sx={externalLabelStyle}>Langfuse Password *</InputLabel><TextField required fullWidth disabled id="langfusePassword" name="langfusePassword" type="password" size="small" placeholder="Enter Langfuse Password" sx={sxTextField} value={langfusePassword} onChange={handleInputChange} /></Box>
//                         <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="clickhouseUrl" sx={externalLabelStyle}>Clickhouse URL *</InputLabel><TextField required fullWidth disabled id="clickhouseUrl" name="clickhouseUrl" size="small" placeholder="Enter Clickhouse URL" sx={sxTextField} value={clickhouseUrl} onChange={handleInputChange} /></Box>
//                     </Box>
//                     <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1.5 }}>
//                         <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="clickhouseDbUsername" sx={externalLabelStyle}>Clickhouse DB Username *</InputLabel><TextField required fullWidth disabled id="clickhouseDbUsername" name="clickhouseDbUsername" size="small" placeholder="Enter Clickhouse DB Username" sx={sxTextField} value={clickhouseDbUsername} onChange={handleInputChange} /></Box>
//                         <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="clickhouseDbPassword" sx={externalLabelStyle}>Clickhouse DB Password *</InputLabel><TextField required fullWidth disabled id="clickhouseDbPassword" name="clickhouseDbPassword" type="password" size="small" placeholder="Enter Clickhouse DB Password" sx={sxTextField} value={clickhouseDbPassword} onChange={handleInputChange} /></Box>
//                     </Box>
//                   </>
//                 )}
//             </Box>
//           </Box>
          
//           <Box sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0, display: 'flex', flexDirection: 'column', backgroundColor:'rgba(243, 248, 254, 1)'}}>
//               <Box sx={{ p: '15px', pb: 0, flexShrink: 0 }}>
//                 <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',backgroundColor:'rgba(228, 240, 253, 1)', p: '12px', borderRadius: '4px' }}>
//                     <Box sx={{ width :'250px', mr: 1 }}>
//                         <Typography variant="h5" sx={{ fontSize:'12px',fontWeight: 600,mb:0.5 }}>Setup Guide</Typography>
//                         <Typography variant="body2" sx={{ fontSize:'11px',fontWeight: 400 }}>Step-by-step guide to set up data access.</Typography>
//                     </Box>
//                     <img src="/icons/step-guide.svg" alt="Setup Guide" style={{ height: '70px', width: '70px', flexShrink: 0 }} />
//                 </Box>
//               </Box>

//               <Box sx={{ flexGrow: 1, overflowY: 'auto', p: '15px', pt: 2 }}>
//                 {selectedProvider === 'google-cloud-platform' && (
//                   <>
//                       {setupLoading && <CircularProgress size={20} sx={{alignSelf: 'center'}} />}
//                       {gcpStepsData.map((item) => (
//                           <Box sx={{ mb: 2 }} textAlign={'left'} key={item.step}>
//                               <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main', textTransform: 'uppercase' }}>{item.step}</Typography>
//                               <Typography sx={{ fontSize: '12px', fontWeight: 600, whiteSpace: 'pre-line' }}>{item.title}</Typography>
//                               <Typography variant="body2" sx={{ fontSize: '11px', color: 'text.secondary', whiteSpace: 'pre-line', wordBreak: 'break-word' }}>{item.description}</Typography>
//                           </Box>
//                       ))}
//                       {setupError && <Alert severity="error">Failed to get Service Account Email: {setupError.message}</Alert>}
//                   </>
//                 )}
//                 {selectedProvider === 'llm' && <LlmSetupGuide />}
//               </Box>
//           </Box>
//         </Box>
//         <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, p: 2, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
//           <Button variant="text" href="https://cloud.google.com/billing/docs/how-to/find-billing-account-id" target="_blank" rel="noopener noreferrer" sx={{ color:'rgba(0, 100, 255, 1)', mr: 'auto', textTransform: 'none', textDecoration: 'none' }} startIcon={<img src="/linkurl.svg" alt="Help icon" width="16" height="16"/>}>
//             Where do I find my Billing Account ID?
//           </Button>
//           <Button sx={{ backgroundColor:' #F2F7FF',textTransform: 'none', fontFamily:'Inter', borderRadius:'8px',borderColor: '#0064FF', color:'#0064FF'}} onClick={() => setIsGoBackDialogOpen(true)}>Cancel</Button>
//           <Button
//             variant="contained"
//             sx={{ textTransform: 'none',fontFamily:'Inter', borderRadius:'8px', backgroundColor:' #0064FF',color:'white' }}
//             onClick={handleSubmit}
//             disabled={!canSubmit || isSubmitting}
//             startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
//           >
//             {isSubmitting ? (validateLoading ? 'Validating...' : 'Adding...') : 'Add Data Source'}
//           </Button>
//         </Box>
//       </Paper>
//       <Snackbar open={isToastOpen} autoHideDuration={6000} onClose={handleToastClose} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
//         <Alert icon={<CustomErrorIcon />} onClose={handleToastClose} sx={{ width: '100%', borderRadius: '50px', backgroundColor: 'white', boxShadow: theme.shadows[3], color: 'text.secondary', alignItems: 'center', p: theme.spacing(0.5, 2), '& .MuiAlert-message': { flex: 1, py: '12px' } }}>
//           {toastMessage}
//         </Alert>
//       </Snackbar>
//       <GoBackDialog open={isGoBackDialogOpen} onClose={() => setIsGoBackDialogOpen(false)} onConfirm={() => navigate('/data-sources')} />
//     </Box>
//   );
// };

// export default AddDataSourcesPage;





// import React, { useState, useMemo, useEffect } from 'react';
// import {
//   Box,
//   Typography,
//   Button,
//   TextField,
//   RadioGroup,
//   FormControlLabel,
//   Radio,
//   Paper,
//   Divider,
//   InputLabel,
//   CircularProgress,
//   Checkbox,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   Snackbar,
//   Alert,
//   Link,
//    InputAdornment,
// } from '@mui/material';
// import { useTheme } from '@mui/material/styles';
// import { alpha } from '@mui/material/styles';
// import { GitHub as GitHubIcon, ErrorOutline as ErrorOutlineIcon, Close as CloseIcon } from '@mui/icons-material';
// import { useNavigate, useOutletContext } from 'react-router-dom';
// import { gql, useLazyQuery, useMutation } from '@apollo/client';
// import type { Tenant } from '../../App';
// import {  Check as CheckIcon } from '@mui/icons-material';


// // This enum should ideally be in a shared types file
// export enum GQLProviderType {
//   GCP = "GCP",langfuse
//   LLM = "LLM",
//   AWS = "AWS",
//   AZURE = "Azure",
// }

// // --- Custom icon for the error toast ---
// const CustomErrorIcon = () => (
//     <Box
//       component="span"
//       sx={{
//         width: 24,
//         height: 24,
//         borderRadius: '50%',
//         display: 'flex',
//         alignItems: 'center',
//         justifyContent: 'center',
//         backgroundColor: '#D92D20',
//       }}
//     >
//       <CloseIcon sx={{ color: 'white', fontSize: '16px' }} />
//     </Box>
// );

// // --- GoBackDialog to confirm leaving the page ---
// const GoBackDialog = ({ open, onClose, onConfirm }: { open: boolean, onClose: () => void, onConfirm: () => void }) => {
//   if (!open) return null;

//   const backgroundTextureUrl = '/textures/dialog-background-texture.svg';
//   const goBackIconUrl = '/icons/go-back-dialog-icon.svg';

//   return (
//     <Dialog
//       open={open}
//       onClose={onClose}
//       PaperProps={{
//         sx: {
//           borderRadius: '24px',
//           width: '100%',
//           maxWidth: '520px',
//           p: 0.5,
//           position: 'relative',
//           overflow: 'hidden',
//           backgroundImage: `url(${backgroundTextureUrl})`,
//           backgroundRepeat: 'no-repeat',
//           backgroundPosition: 'top right',
//           backgroundColor: 'white',
//         },
//       }}
//     >
//       <DialogContent sx={{ p: 3, pt: 4, zIndex: 1 }}>
//         <Box
//           sx={{
//             mb: 3,
//             width: 50,
//             height: 50,
//             borderRadius: '12px',
//             display: 'flex',
//             alignItems: 'center',
//             justifyContent: 'center',
//             backgroundColor:' #E5EFFF'
//           }}
//         >
//           <img src={goBackIconUrl} alt="Go back icon" style={{ width: 19, height: 20 }} />
//         </Box>
//         <Typography sx={{ fontWeight: 600, mb: 1, fontSize: '16px', color: '#333333' }}>
//           Go Back
//         </Typography>
//         <Typography variant="body1" sx={{ color: ' #333333', fontSize: '14px', lineHeight: 1.6, fontWeight:400 }}>
//           Are you sure you want to leave this page? Leaving this page
//           will discard all the progress made in the below form.
//         </Typography>
//       </DialogContent>
//       <DialogActions sx={{ p: 3, justifyContent: 'flex-end', gap: 1.5, zIndex: 1 }}>
//         <Button
//           onClick={onClose}
//           sx={{
//             textTransform: 'none',
//             borderRadius: '8px',
//             backgroundColor: '#EFF6FF',
//             color: '#0064FF',
//             fontWeight: 500,
//             fontSize: '14px',
//             padding: '10px 20px',
//           }}
//         >
//           Cancel
//         </Button>
//         <Button
//           onClick={onConfirm}
//           size="large"
//           sx={{
//             textTransform: 'none',
//             borderRadius: '8px',
//             backgroundColor: '#0064FF',
//             fontWeight: 500,
//             fontSize: '14px',
//             padding: '10px 20px',
//             boxShadow: 'none',
//             color:'white'
//           }}
//         >
//           Yes, Go Back
//         </Button>
//       </DialogActions>
//     </Dialog>
//   );
// };

// // --- START: New component for the LLM Setup Guide ---
// const LlmSetupGuide = () => {
//     const [activeManager, setActiveManager] = useState('npm');

//     const commands = {
//         npm: 'npm install langfuse\n\n# Node.js < 18\nnpm install langfuse-node',
//         pnpm: 'pnpm add langfuse\n\n# Node.js < 18\npnpm add langfuse-node',
//         yarn: 'yarn add langfuse\n\n# Node.js < 18\nyarn add langfuse-node',
//         bun: 'bun add langfuse\n\n# Node.js < 18\nbun add langfuse-node',
//     };

//     const Badge = ({ label, value, color, href }: { label: string, value: string, color: string, href?: string }) => (
//         <Button
//             component={href ? 'a' : 'button'}
//             href={href}
//             target="_blank"
//             rel="noopener noreferrer"
//             sx={{
//                 p: 0,
//                 m: 0,
//                 height: '20px',
//                 textTransform: 'none',
//                 borderRadius: '4px',
//                 overflow: 'hidden',
//                 '&:hover': {
//                     ...(href && {
//                         opacity: 0.8
//                     })
//                 }
//             }}>
//             <Box sx={{ bgcolor: '#555', color: 'white', px: 1, height: '100%', display: 'flex', alignItems: 'center' }}>
//                 {label === 'repo' && <GitHubIcon sx={{ fontSize: '14px', mr: 0.5 }} />}
//                 <Typography sx={{ fontSize: '10px', fontWeight: 500 }}>{label}</Typography>
//             </Box>
//             <Box sx={{ bgcolor: color, color: 'white', px: 1, height: '100%', display: 'flex', alignItems: 'center' }}>
//                 <Typography sx={{ fontSize: '10px', fontWeight: 500 }}>{value}</Typography>
//             </Box>
//         </Button>
//     );

//     const TabButton = ({ manager }: { manager: 'npm' | 'pnpm' | 'yarn' | 'bun' }) => (
//         <Button
//             onClick={() => setActiveManager(manager)}
//             sx={{
//                 p: '2px 8px',
//                 minWidth: 'auto',
//                 color: activeManager === manager ? 'primary.main' : 'text.secondary',
//                 fontWeight: 500,
//                 fontSize: '13px',
//                 borderBottom: 2,
//                 borderColor: activeManager === manager ? 'primary.main' : 'transparent',
//                 borderRadius: 0,
//                 textTransform: 'none'
//             }}
//         >
//             {manager}
//         </Button>
//     );

//     return (
//         <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}><Typography sx={{ fontWeight: 600, fontSize: '14px' }}>Langfuse SDK Setup</Typography>
//           <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
//               The Langfuse SDKs are the recommended way to integrate with Langfuse.{' '}
//               <Link 
//                 href="https://langfuse.com/integrations" 
//                 target="_blank" 
//                 rel="noopener noreferrer"
//                 sx={{ fontWeight: 500 }}
//               >
//                 [See a full list of Integration]
//               </Link>
//           </Typography>

//             {/* <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>Langfuse SDK Setup</Typography>
//             <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
//                 The Langfuse SDKs are the recommended way to integrate with Langfuse. 
//             </Typography> */}
//             {/* <Typography sx={{ fontSize: '12px', color: 'text.primary', fontWeight: 500, mt: 1 }}>
//                 Properties:
//             </Typography>
//             <Box component="ul" sx={{ pl: 2.5, m: 0, fontSize: '12px', color: 'text.secondary', '& li': { mb: 0.5 } }}>
//                 <li>Fully async requests, using Langfuse adds almost no latency</li>
//                 <li>Accurate latency tracking using synchronous timestamps</li>
//                 <li>IDs available for downstream use</li>
//                 <li>Great DX when nesting observations</li>
//                 <li>Cannot break your application, all errors are caught and logged</li>
//                 <li>Interoperable with Langfuse <Link href="#" underline="hover">(Integrations Link)</Link></li>
//             </Box> */}
//              <Divider sx={{ my: 1 }} />
//                         <Typography sx={{ fontSize: '12px', color: '#333333', fontWeight:600 }}>
//                  Install the Toolkit
//             </Typography>
           

           
//             <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
//                 pick your language and run one of these commands in your project's terminal
//             </Typography>

//             <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>JS/TS</Typography>
//             <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
//                 <Badge label="repo" value="langfuse-js" color="#007ec6" href="https://github.com/langfuse/langfuse-js" />
//                 <Badge label="npm langfuse" value="v3.38.4" color="#007ec6" />
//                 <Badge label="npm langfuse-node" value="v3.38.4" color="#007ec6" />
//             </Box>
//             <Box sx={{ display: 'flex', borderBottom: 1, borderColor: 'divider' }}>
//                 <TabButton manager="npm" />
//                 <TabButton manager="pnpm" />
//                 <TabButton manager="yarn" />
//                 <TabButton manager="bun" />
//             </Box>
//             <Box sx={{
//                 bgcolor: '#f7f7f7',
//                 p: 1.5,
//                 borderRadius: '4px',
//                 fontFamily: 'monospace',
//                 whiteSpace: 'pre-wrap',
//                 fontSize: '12px',
//                 border: '1px solid #e0e0e0'
//             }}>
//                 {commands[activeManager as keyof typeof commands]}
//             </Box>

//             <Divider sx={{ my: 2 }} />

//             <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>Python</Typography>
//             <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
//                 <Badge label="repo" value="langfuse-python" color="#007ec6" href="https://github.com/langfuse/langfuse-python" />
//                 <Badge label="pypi langfuse" value="v3.2.3" color="#007ec6" />
//             </Box>
//             <Box sx={{
//                 bgcolor: '#f7f7f7',
//                 p: 1.5,
//                 borderRadius: '4px',
//                 fontFamily: 'monospace',
//                 whiteSpace: 'pre-wrap',
//                 fontSize: '12px',
//                 border: '1px solid #e0e0e0',
//                 mt: 1
//             }}>
//                 pip install langfuse
//             </Box>
//             <Box component="ul" sx={{ pl: 2.5, m: 0, mt: 1, fontSize: '12px', color: 'text.secondary', '& li': { mb: 0.5 } }}>
//                 <li>Based on OpenTelemetry, so you can use any OTEL-based instrumentation library.</li>
//                 <li>Uses Pydantic for typing and validation.</li>
//                 <li>
//                     Interfaces:
//                     <Box component="ul" sx={{ pl: 2.5, m: 0, mt: 0.5 }}>
//                         <li><Link href="https://langfuse.com/docs/observability/sdk/python/sdk-v3#observe-decorator" underline="hover">@observe() decorator</Link></li>
//                         <li><Link href="https://langfuse.com/docs/observability/sdk/python/sdk-v3#context-managers" underline="hover">Context Managers</Link></li>
//                         <li><Link href="https://langfuse.com/docs/observability/sdk/python/sdk-v3#manual-observations" underline="hover">Manual instrumentation</Link></li>
//                     </Box>
//                 </li>
//             </Box>
//         </Box>
//     );
// };
// // --- END: New component ---

// // --- GraphQL Definitions ---
// const GET_TENANT_PROVIDER_ACCESS = gql`
//   query GetTenantProviderAccess($tenantId: ID!, $providerType: ProviderType!) {
//     getTenantProviderAccess(tenantId: $tenantId, providerType: $providerType) {
//       serviceAccountEmail
//     }
//   }
// `;

// const CREATE_DATA_SOURCE_MUTATION = gql`
//   mutation CreateDataSource($input: CreateDataSourceInput!) {
//     createDataSource(input: $input) {
//       id
//       name
//     }
//   }
// `;

// const VALIDATE_NEW_DATA_SOURCE_CREDENTIALS_MUTATION = gql`
//   mutation ValidateNewDataSourceCredentials($input: ValidateNewDataSourceCredentialsInput!) {
//     validateNewDataSourceCredentials(input: $input) {
//       isValid
//       validationErrors
//     }
//   }
// `;

// const mapProviderToGqlType = (frontendProviderValue: string): GQLProviderType => {
//   switch (frontendProviderValue) {
//     case 'google-cloud-platform': return GQLProviderType.GCP;
//     case 'llm': return GQLProviderType.LLM;
//     case 'aws': return GQLProviderType.AWS;
//     case 'azure': return GQLProviderType.AZURE;
//     default: throw new Error(`Unsupported provider value: ${frontendProviderValue}`);
//   }
// };

// interface AddDataSourceOutletContext {
//   currentTenant: Tenant | null;
// }

// const AddDataSourcesPage: React.FC = () => {
//   const theme = useTheme();
//   const navigate = useNavigate();
//   const { currentTenant } = useOutletContext<AddDataSourceOutletContext>();
//   const tenantId = currentTenant?.id;

//   // Form State
//   const [name, setName] = useState('');
//   const [billingAccountId, setBillingAccountId] = useState('');
//   const [bigQueryProjectId, setBigQueryProjectId] = useState('');
//   const [bigQueryDatasetId, setBigQueryDatasetId] = useState('');
//   const [bigQueryDataTableName, setBigQueryDataTableName] = useState('');
//   const [selectedProvider, setSelectedProvider] = useState('google-cloud-platform');
//   const [fieldErrors, setFieldErrors] = useState({ name: '', billingAccountId: '', bigQueryProjectId: '', bigQueryDatasetId: '', bigQueryDataTableName: '' });
//   const [permissionsGranted, setPermissionsGranted] = useState(false);

//   // Other component state
//   const [serviceAccountEmail, setServiceAccountEmail] = useState<string | null>(null);
//   const [isGoBackDialogOpen, setIsGoBackDialogOpen] = useState(false);
//   const [isToastOpen, setIsToastOpen] = useState(false);
//   const [toastMessage, setToastMessage] = useState('');

//   // Hooks for GraphQL operations
//   const [getTenantAccess, { loading: setupLoading, error: setupError }] = useLazyQuery(GET_TENANT_PROVIDER_ACCESS);
//   const [createDataSource, { loading: createLoading }] = useMutation(CREATE_DATA_SOURCE_MUTATION);
//   const [validateCredentials, { loading: validateLoading }] = useMutation(VALIDATE_NEW_DATA_SOURCE_CREDENTIALS_MUTATION);

//   useEffect(() => {
//     setBillingAccountId('');
//     setBigQueryProjectId('');
//     setBigQueryDatasetId('');
//     setBigQueryDataTableName('');
//     setPermissionsGranted(false);
//     setFieldErrors(prev => ({ name: prev.name, billingAccountId: '', bigQueryProjectId: '', bigQueryDatasetId: '', bigQueryDataTableName: '' }));

//     if (tenantId && selectedProvider === 'google-cloud-platform') {
//         getTenantAccess({
//             variables: { tenantId, providerType: GQLProviderType.GCP },
//             onCompleted: (data) => setServiceAccountEmail(data.getTenantProviderAccess?.serviceAccountEmail || null),
//             onError: (error) => console.error("Failed to fetch service account email:", error),
//         });
//     } else {
//         setServiceAccountEmail(null);
//     }
//   }, [selectedProvider, tenantId, getTenantAccess]);

//   const gcpStepsData = useMemo(() => {
//     return [
//         { step: 'STEP 1', title: 'Enable billing export to BigQuery', description: 'Go to Billing > Billing Export > Export to BigQuery > Choose Dataset' },
//         {
//           step: 'STEP 2',
//           title: 'Assign Roles to Service Account',
//           description: serviceAccountEmail
//             ? `Give the following roles:\n * BigQuery Data Viewer\n * BigQuery Job User\nTo: The service account (${serviceAccountEmail})`
//             : (setupLoading ? 'Fetching service account details...' : 'Could not retrieve service account.')
//         },
//         { step: 'STEP 3', title: 'Provide the following information below', description: 'Enter your GCP credentials and dataset details' },
//     ];
//   }, [serviceAccountEmail, setupLoading]);

//   const externalLabelStyle = { fontFamily: 'Inter, sans-serif', fontSize: '12px', fontWeight: 400, color: theme.palette.text.primary, marginBottom: theme.spacing(0.75), display: 'block' };
//   const sxTextField = {
//   '& .MuiOutlinedInput-root': {
//     '& fieldset': { borderRadius: '8px' },
//     '&.Mui-focused fieldset': { borderColor: 'rgba(0, 0, 0, 0.23)' },
//   },
//   '& .MuiInputBase-input': {
//     fontFamily: 'Inter, sans-serif',
//     color: theme.palette.text.primary,
//     fontSize: '12px',
//     '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
//       WebkitBoxShadow: '0 0 0 30px white inset !important',
//       WebkitTextFillColor: `${theme.palette.text.primary} !important`,
//       transition: 'background-color 5000s ease-in-out 0s',
//     },
//   },
//   '& .MuiInputBase-input::placeholder': {
//     fontFamily: 'Inter, sans-serif',
//     color: 'rgba(185, 185, 185, 1)',
//     fontSize: '12px',
//     opacity: 1,
//   },
// };

//   const validateField = (name: string, value: string): string => {
//     if (selectedProvider === 'llm' && name !== 'name') {
//         return '';
//     }
//     switch (name) {
//       case 'name':
//         if (!/^[a-z0-9\s]{2,50}$/.test(value)) return 'Must be 2-50 chars, lowercase letters, numbers, spaces.';
//         break;
//       case 'billingAccountId':
//         if (!/^[0-9A-F]{6}-[0-9A-F]{6}-[0-9A-F]{6}$/i.test(value)) return 'Format: XXXXXX-XXXXXX-XXXXXX (hex digits).';
//         break;
//       case 'bigQueryProjectId':
//         if (!/^[a-z][a-z0-9-]{4,28}[a-z0-9]$/.test(value)) return '6-30 chars, starts with letter, ends with letter/num, only lowercase, nums, hyphens.';
//         break;
//       case 'bigQueryDatasetId':
//         if (!/^[a-zA-Z][a-zA-Z0-9_]{0,1023}$/.test(value)) return '1-1024 chars, starts with letter, only letters, nums, underscores.';
//         break;
//       case 'bigQueryDataTableName':
//         if (!/^[a-zA-Z_][a-zA-Z0-9_-]{0,1023}$/.test(value)) {
//           return '1-1024 chars, starts with letter or underscore, can only contain letters, nums, underscores, or hyphens.';
//         }
//         const prefixes = ['_table_', '_file_', '_partition_'];
//         if (prefixes.some(p => value.toLowerCase().startsWith(p))) {
//           return 'Cannot start with _TABLE_, _FILE_, or _PARTITION_.';
//         }
//         break;
//       default:
//         break;
//     }
//     return '';
//   };

//   const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
//     const { name, value } = event.target;
//     let errorMessage = validateField(name, value);
//     switch (name) {
//       case 'name': setName(value); break;
//       case 'billingAccountId': setBillingAccountId(value); break;
//       case 'bigQueryProjectId': setBigQueryProjectId(value); break;
//       case 'bigQueryDatasetId': setBigQueryDatasetId(value); break;
//       case 'bigQueryDataTableName': setBigQueryDataTableName(value); break;
//       default: break;
//     }
//     setFieldErrors(prev => ({ ...prev, [name]: errorMessage }));
//   };

//   const handleGoBack = () => navigate('/data-sources');
//   const handleProviderChange = (event: React.ChangeEvent<HTMLInputElement>) => setSelectedProvider(event.target.value);

//   const canSubmit = useMemo(() => {
//     const hasNoErrors = Object.values(fieldErrors).every(error => error === '');
//     const baseChecks = name.trim() !== '' && !!tenantId && hasNoErrors;
//     if (selectedProvider === 'llm') {
//         return baseChecks;
//     }
//     if (selectedProvider === 'google-cloud-platform') {
//         const gcpChecks =
//             billingAccountId.trim() !== '' &&
//             bigQueryProjectId.trim() !== '' &&
//             bigQueryDatasetId.trim() !== '' &&
//             bigQueryDataTableName.trim() !== '' &&
//             permissionsGranted &&
//             !!serviceAccountEmail;
//         return baseChecks && gcpChecks;
//     }
//     return false;
//   }, [
//     name, tenantId, billingAccountId, bigQueryProjectId, bigQueryDatasetId, bigQueryDataTableName,
//     permissionsGranted, selectedProvider, serviceAccountEmail, fieldErrors
//   ]);

//   const handleToastClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
//     if (reason === 'clickaway') return;
//     setIsToastOpen(false);
//   };

//   const handleSubmit = async () => {
//     if (!canSubmit) return;
//     let validationInput: any = {
//       name: name.trim(),
//       tenantId,
//       providerType: mapProviderToGqlType(selectedProvider),
//     };
//     if (selectedProvider === 'google-cloud-platform') {
//         validationInput = {
//             ...validationInput,
//             billingAccountId: billingAccountId.trim(),
//             bigQueryProjectId: bigQueryProjectId.trim(),
//             bigQueryDatasetId: bigQueryDatasetId.trim(),
//             bigQueryDataTableName: bigQueryDataTableName.trim(),
//         }
//     }
//     try {
//       const validationResponse = await validateCredentials({ variables: { input: validationInput } });
//       const validationResult = validationResponse.data?.validateNewDataSourceCredentials;
//       if (!validationResult?.isValid) {
//         setToastMessage("We are unable to add Data source at this moment please try again after some time.");
//         setIsToastOpen(true);
//         return;
//       }
//       const createResponse = await createDataSource({ variables: { input: validationInput } });
//       if (createResponse.errors && createResponse.errors.length > 0) {
//         setToastMessage("We are unable to add Data source at this moment please try again after some time.");
//         setIsToastOpen(true);
//         return;
//       }
//       navigate('/data-sources', { state: { dataSourceAdded: true } });
//     } catch (e: any) {
//       setToastMessage("We are unable to add Data source at this moment please try again after some time.");
//       setIsToastOpen(true);
//     }
//   };

//   const isSubmitting = validateLoading || createLoading;

//   if (!tenantId) {
//     return (
//       <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', p: 3 }}>
//         <Alert severity="error">Required tenant information is missing.</Alert>
//         <Typography sx={{mt: 2}}>This page must be accessed from the Data Sources list.</Typography>
//         <Button onClick={handleGoBack} sx={{ mt: 2 }}>Go Back</Button>
//       </Box>
//     );
//   }

//   return (
//     <Box sx={{ p: { xs: 1, sm: 2.5 } }}>
//       <Button
//         onClick={() => setIsGoBackDialogOpen(true)}
//         startIcon={<img src="/icons/back-arrow.svg" alt="Back" style={{ width: '12px', height: '12px', marginLeft:5, marginRight:3 }} />}
//         sx={{ p: 0, textTransform: 'none', color: 'text.primary', '&:hover': { backgroundColor: 'transparent' } }}
//       >
//         <Typography sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '14px' }}>
//           Add New Data Sources
//         </Typography>
//       </Button>
//       <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '12px', color: 'rgba(102, 102, 102, 1)', mt: 0.5, marginLeft:2 }}>
//         Add the Billing Account IDs and linked BigQuery dataset details associated with organisation.
//       </Typography>

//       <Paper
//         elevation={0}
//         sx={{ height: { md: 'calc(100vh - 180px)' }, minHeight: '650px', borderRadius: '10px', backgroundColor: 'white', display: 'flex', flexDirection: 'column', overflow: 'hidden', mt: 2 }}
//       >
//         <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: theme.spacing(4), flexGrow: 1, overflow: 'auto' }}>
//           <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: theme.spacing(3), padding: theme.spacing(3) }}>
//             <Typography variant="h6" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '14px' }}>Select Provider</Typography>
//             <RadioGroup row value={selectedProvider} onChange={handleProviderChange} name="select-provider-group" sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', marginLeft:'10px' }}>
//               <FormControlLabel value="google-cloud-platform" control={<Radio sx={{ display: 'none' }} />} label={
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1, py: 1, borderRadius: '8px', border: `1px solid ${selectedProvider === 'google-cloud-platform' ? theme.palette.primary.main : theme.palette.grey[300]}`, backgroundColor: selectedProvider === 'google-cloud-platform' ? alpha(theme.palette.primary.main, 0.05) : theme.palette.grey[100], width: '255px', height: '35px', cursor: 'pointer' }}>
//                   <img src="/icons/google-cloud-icon-2048x1646-7admxejz.svg" alt="GCP" style={{ height: 20, width: 20 }} />
//                   <Typography sx={{ flexGrow: 1, fontSize: '12px' }}>Google Cloud Platform</Typography>
//                   <Box sx={{ ml: 'auto', width: 16, height: 16, borderRadius: '50%', border: `1px solid ${selectedProvider === 'google-cloud-platform' ? theme.palette.primary.main : theme.palette.text.disabled}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, '&::before': { content: '""', width: 8, height: 8, borderRadius: '50%', backgroundColor: selectedProvider === 'google-cloud-platform' ? theme.palette.primary.main : 'transparent' } }} />
//                 </Box>
//               }/>
//               <FormControlLabel value="llm" control={<Radio sx={{ display: 'none' }} />} label={
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1, py: 1, borderRadius: '8px', border: `1px solid ${selectedProvider === 'llm' ? theme.palette.primary.main : theme.palette.grey[300]}`, backgroundColor: selectedProvider === 'llm' ? alpha(theme.palette.primary.main, 0.05) : theme.palette.grey[100], width: '255px', height: '35px', cursor: 'pointer' }}>
//                   <img src="/icons/llm-observability-blue.svg" alt="LLM" style={{ height: 18, width: 18}} />
//                   <Typography sx={{ flexGrow: 1, fontSize: '12px' }}>LLM</Typography>
//                   <Box sx={{ ml: 'auto', width: 16, height: 16, borderRadius: '50%', border: `1px solid ${selectedProvider === 'llm' ? theme.palette.primary.main : theme.palette.text.disabled}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, '&::before': { content: '""', width: 8, height: 8, borderRadius: '50%', backgroundColor: selectedProvider === 'llm' ? theme.palette.primary.main : 'transparent' } }} />
//                 </Box>
//               }/>
//               <FormControlLabel value="aws" control={<Radio sx={{ display: 'none' }} />} disabled={true} label={
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1, py: 1, borderRadius: '8px', border: `1px solid ${theme.palette.grey[300]}`, backgroundColor: theme.palette.grey[100], width: '255px', height: '35px', cursor: 'not-allowed', opacity: 0.5 }}>
//                   <img src="/icons/aws.png" alt="AWS" style={{ height: 20, width: 20 }} />
//                   <Typography sx={{ flexGrow: 1, fontSize: '12px' }}>Amazon Web Services</Typography>
//                   <Box sx={{ ml: 'auto', width: 16, height: 16, borderRadius: '50%', border: `1px solid ${theme.palette.text.disabled}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} />
//                 </Box>
//               }/>
//               <FormControlLabel value="azure" control={<Radio sx={{ display: 'none' }} />} disabled={true} label={
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1, py: 1, borderRadius: '8px', border: `1px solid ${theme.palette.grey[300]}`, backgroundColor: theme.palette.grey[100], width: '255px', height: '35px', cursor: 'not-allowed', opacity: 0.5 }}>
//                   <img src="/icons/azure.svg" alt="Azure" style={{ height: 20, width: 20 }} />
//                   <Typography sx={{ flexGrow: 1, fontSize: '12px' }}>Microsoft Azure</Typography>
//                   <Box sx={{ ml: 'auto', width: 16, height: 16, borderRadius: '50%', border: `1px solid ${theme.palette.text.disabled}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} />
//                 </Box>
//               }/>
//             </RadioGroup>
//             <Divider />
//             <Typography sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '14px',color:' #333333' }}>
//                 {selectedProvider === 'llm' ? 'LLM Details' : 'Billing Account Details'}
//             </Typography>

//             <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0 }}>
//                 {selectedProvider === 'google-cloud-platform' && (
//                     <>
//                         <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1.5 }}>
//                             <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="dataSourceName" sx={externalLabelStyle}>Data Source Name *</InputLabel><TextField autoFocus required fullWidth id="dataSourceName" name="name" size="small" placeholder="Enter your data source name" sx={sxTextField} value={name} onChange={handleInputChange} error={!!fieldErrors.name} helperText={fieldErrors.name} InputProps={{ endAdornment: name && !fieldErrors.name && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} /></Box>
//                             <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="billingAccountId" sx={externalLabelStyle}>Billing Account ID *</InputLabel><TextField required fullWidth id="billingAccountId" name="billingAccountId" size="small" placeholder="Enter your GCP billing account ID" sx={sxTextField} value={billingAccountId} onChange={handleInputChange} error={!!fieldErrors.billingAccountId} helperText={fieldErrors.billingAccountId} InputProps={{ endAdornment: billingAccountId && !fieldErrors.billingAccountId && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} /></Box>
//                         </Box>
//                         <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1.5 }}>
//                             <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="bigQueryProjectId" sx={externalLabelStyle}>BigQuery Project ID *</InputLabel><TextField required fullWidth id="bigQueryProjectId" name="bigQueryProjectId" size="small" placeholder="Enter your BigQuery project ID" sx={sxTextField} value={bigQueryProjectId} onChange={handleInputChange} error={!!fieldErrors.bigQueryProjectId} helperText={fieldErrors.bigQueryProjectId} InputProps={{ endAdornment: bigQueryProjectId && !fieldErrors.bigQueryProjectId && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} /></Box>
//                             <Box sx={{ flex: 1, flexDirection: 'column' }}><InputLabel htmlFor="bigQueryDatasetId" sx={externalLabelStyle}>BigQuery Dataset ID *</InputLabel><TextField required fullWidth id="bigQueryDatasetId" name="bigQueryDatasetId" size="small" placeholder="Enter your BigQuery dataset ID" sx={sxTextField} value={bigQueryDatasetId} onChange={handleInputChange} error={!!fieldErrors.bigQueryDatasetId} helperText={fieldErrors.bigQueryDatasetId} InputProps={{ endAdornment: bigQueryDatasetId && !fieldErrors.bigQueryDatasetId && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} /></Box>
//                         </Box>
//                         <Box sx={{ mt: 1.5, width:'49%' }}><InputLabel htmlFor="bigQueryDataTableName" sx={externalLabelStyle}>BigQuery Table Name *</InputLabel><TextField required fullWidth id="bigQueryDataTableName" name="bigQueryDataTableName" size="small" placeholder="Enter your BigQuery table name" sx={sxTextField} value={bigQueryDataTableName} onChange={handleInputChange} error={!!fieldErrors.bigQueryDataTableName} helperText={fieldErrors.bigQueryDataTableName} InputProps={{ endAdornment: bigQueryDataTableName && !fieldErrors.bigQueryDataTableName && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} /></Box>
//                         <FormControlLabel control={<Checkbox checked={permissionsGranted} onChange={(e) => setPermissionsGranted(e.target.checked)}/>} label= {<Typography sx={{ fontFamily: 'Inter', fontSize: '14px', color: 'text.secondary' }}>I have granted the necessary permissions as described in the Setup Guide.</Typography>} disabled={!serviceAccountEmail || setupLoading}/>
//                     </>
//                 )}
//                 {selectedProvider === 'llm' && (
//                     <Box sx={{ mt: 1.5, width: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
//                         <InputLabel htmlFor="dataSourceName" sx={externalLabelStyle}>Data Source Name *</InputLabel>
//                         <TextField autoFocus required fullWidth id="dataSourceName" name="name" size="small" placeholder="Enter your data source name" sx={sxTextField} value={name} onChange={handleInputChange} error={!!fieldErrors.name} helperText={fieldErrors.name} InputProps={{ endAdornment: name && !fieldErrors.name && ( <InputAdornment position="end"><CheckIcon sx={{ fontSize: '18px', color: '#03A65A' }} /></InputAdornment>)}} />
//                     </Box>
//                 )}
//             </Box>
//           </Box>
          
//           <Box sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0, display: 'flex', flexDirection: 'column', backgroundColor:'rgba(243, 248, 254, 1)'}}>
//               <Box sx={{ p: '15px', pb: 0, flexShrink: 0 }}>
//                 <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',backgroundColor:'rgba(228, 240, 253, 1)', p: '12px', borderRadius: '4px' }}>
//                     <Box sx={{ width :'250px', mr: 1 }}>
//                         <Typography variant="h5" sx={{ fontSize:'12px',fontWeight: 600,mb:0.5 }}>Setup Guide</Typography>
//                         <Typography variant="body2" sx={{ fontSize:'11px',fontWeight: 400 }}>Step-by-step guide to set up data access.</Typography>
//                     </Box>
//                     <img src="/icons/step-guide.svg" alt="Setup Guide" style={{ height: '70px', width: '70px', flexShrink: 0 }} />
//                 </Box>
//               </Box>

//               <Box sx={{ flexGrow: 1, overflowY: 'auto', p: '15px', pt: 2 }}>
//                 {selectedProvider === 'google-cloud-platform' && (
//                   <>
//                       {setupLoading && <CircularProgress size={20} sx={{alignSelf: 'center'}} />}
//                       {gcpStepsData.map((item) => (
//                           <Box sx={{ mb: 2 }} textAlign={'left'} key={item.step}>
//                               <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main', textTransform: 'uppercase' }}>{item.step}</Typography>
//                               <Typography sx={{ fontSize: '12px', fontWeight: 600, whiteSpace: 'pre-line' }}>{item.title}</Typography>
//                               <Typography variant="body2" sx={{ fontSize: '11px', color: 'text.secondary', whiteSpace: 'pre-line', wordBreak: 'break-word' }}>{item.description}</Typography>
//                           </Box>
//                       ))}
//                       {setupError && <Alert severity="error">Failed to get Service Account Email: {setupError.message}</Alert>}
//                   </>
//                 )}

//                 {selectedProvider === 'llm' && <LlmSetupGuide />}
//               </Box>
//           </Box>
//         </Box>
//         <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, p: 2, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
//           <Button variant="text" href="https://cloud.google.com/billing/docs/how-to/find-billing-account-id" target="_blank" rel="noopener noreferrer" sx={{ color:'rgba(0, 100, 255, 1)', mr: 'auto', textTransform: 'none', textDecoration: 'none' }} startIcon={<img src="/linkurl.svg" alt="Help icon" width="16" height="16"/>}>
//             Where do I find my Billing Account ID?
//           </Button>
//           <Button sx={{ backgroundColor:' #F2F7FF',textTransform: 'none', fontFamily:'Inter', borderRadius:'8px',borderColor: '#0064FF', color:'#0064FF'}} onClick={() => setIsGoBackDialogOpen(true)}>Cancel</Button>
//           <Button
//             variant="contained"
//             sx={{ textTransform: 'none',fontFamily:'Inter', borderRadius:'8px', backgroundColor:' #0064FF',color:'white' }}
//             onClick={handleSubmit}
//             disabled={!canSubmit || isSubmitting}
//             startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
//           >
//             {isSubmitting ? (validateLoading ? 'Validating...' : 'Adding...') : 'Add Data Source'}
//           </Button>
//         </Box>
//       </Paper>
//       <Snackbar open={isToastOpen} autoHideDuration={6000} onClose={handleToastClose} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
//         <Alert icon={<CustomErrorIcon />} onClose={handleToastClose} sx={{ width: '100%', borderRadius: '50px', backgroundColor: 'white', boxShadow: theme.shadows[3], color: 'text.secondary', alignItems: 'center', p: theme.spacing(0.5, 2), '& .MuiAlert-message': { flex: 1, py: '12px' } }}>
//           {toastMessage}
//         </Alert>
//       </Snackbar>
//       <GoBackDialog open={isGoBackDialogOpen} onClose={() => setIsGoBackDialogOpen(false)} onConfirm={() => navigate('/data-sources')} />
//     </Box>
//   );
// };

// export default AddDataSourcesPage;